import React, { useState, useEffect, useRef, useCallback } from 'react';
import bridge from '@vkontakte/vk-bridge';
import { TermsPanel } from './TermsPanel';
import { PrivacyPanel } from './PrivacyPanel';
import { SharedWorldMode } from './SharedWorldMode';
import './App.css';
import {
  View,
  Panel,
  PanelHeader,
  Group,
  Cell,
  Avatar,
  Button,
  Textarea,
  Div,
  Text,
  Spinner,
  Banner,
  PanelHeaderBack,
  Placeholder,
  Counter,
  SplitLayout,
  SplitCol,
  ScreenSpinner,
  useAdaptivityConditionalRender,
  Input,
  Image,
  Header,
  Snackbar
} from '@vkontakte/vkui';
import {
  Icon28DeleteOutline,
  Icon28ErrorOutline,
  Icon28GameOutline,
  Icon28GlobeOutline,
  Icon28MarketOutline,
  Icon28MessageOutline,
  Icon28ReplyOutline,
  Icon28Send,
  Icon28UsersOutline,
} from '@vkontakte/icons';
import {
  authUser,
  getCharacters,
  sendMessage,
  getProfile,
  selectCharacter,
  updateUserState,
  checkSession,
  getRoleplayRules,
  saveRoleplayRules,
  getChatHistory,
  resetDialog,
  undoLast,
  initMission,
  getPaymentItems,
  getSubscriptions,
  claimDailyBonus,
  getReferralInfo,
  activateReferral,
  shareReferral,
  enableNotifications,
  deleteUser,
  confirmAccess,
  getCustomCharacters,
  getCustomCharacter,
  createCustomCharacter,
  updateCustomCharacter,
  deleteCustomCharacter,
  getCharacterSlots,
  createCharacterSlot,
  selectCharacterSlot,
  renameCharacterSlot,
  deleteCharacterSlot,
  getSharedWorldStatus,
  enterSharedWorld,
  leaveSharedWorldEvent,
  acknowledgeSharedWorldNotification,
} from './api';

const CUSTOM_DIFFICULTY_DESCRIPTIONS = {
  standard: 'Спокойное общение без дополнительного давления на сюжет.',
  balanced: 'Мир и персонажи активнее двигают историю и создают последствия.',
  simulation: 'Строгая логика времени, расстояний, ресурсов и знаний персонажей.',
  epic: 'Масштабные события, фракции, политика и заметная реакция мира.',
  dark: 'Мрачная взрослая атмосфера, недоверие и тяжёлые решения.',
  grimm: 'Жестокий и тревожный мир, где доверие и победу нужно заслужить.',
  hardcore: 'Максимальная сложность, скрытые угрозы, предательства и окончательная смерть.',
};

const getCharacterAvatarUrl = (characterId) => `/api/character_avatar/${characterId}`;

const CharacterAvatar = ({ character, size = 48, lazy = false }) => {
  const custom = character?.isCustom || String(character?.id || '').startsWith('custom:');
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      overflow: 'hidden',
      background: 'var(--vkui--color_background_secondary)',
      flexShrink: 0,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: Math.max(16, Math.round(size * 0.45)),
    }}>
      <span aria-hidden="true">{custom ? '🧩' : '👤'}</span>
      {!custom && character?.id && (
        <img
          src={getCharacterAvatarUrl(character.id)}
          width={size}
          height={size}
          loading={lazy ? 'lazy' : undefined}
          decoding={lazy ? 'async' : undefined}
          style={{
            position: 'absolute',
            inset: 0,
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            objectPosition: 'top center',
            display: 'block',
          }}
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      )}
    </div>
  );
};

const App = () => {
  const [activePanel, setActivePanel] = useState('main');
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [missions, setMissions] = useState([]);
  const [gameMode, setGameMode] = useState('characters');
  const [currentMission, setCurrentMission] = useState(null); // Текущая миссия (если выбрана)
  const [expandedCategories, setExpandedCategories] = useState(new Set(['base'])); // По умолчанию раскрыта категория "Основные"
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set()); // Раскрытые подкатегории
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sharedWorldBinding, setSharedWorldBinding] = useState(null);
  const [sharedWorldOverview, setSharedWorldOverview] = useState(null);
  const [sharedWorldLoading, setSharedWorldLoading] = useState(false);
  const [sharedWorldActionLoading, setSharedWorldActionLoading] = useState(false);
  const [sharedWorldError, setSharedWorldError] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vkUserId, setVkUserId] = useState(null);
  const [popout, setPopout] = useState(null);
  const [usernameModal, setUsernameModal] = useState(null); // Модальное окно для username
  const [missionModal, setMissionModal] = useState(null); // Модальное окно для миссии
  const [pendingCharacter, setPendingCharacter] = useState(null); // Выбранный персонаж ожидающий username
  const [characterSlots, setCharacterSlots] = useState([]);
  const [characterSlotLimit, setCharacterSlotLimit] = useState(2);
  const [activeSlotId, setActiveSlotId] = useState(0);
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [slotTitleInput, setSlotTitleInput] = useState('');
  const [customCharacters, setCustomCharacters] = useState([]);
  const [customCharacterLimit, setCustomCharacterLimit] = useState(2);
  const [customCharacterPromptLimit, setCustomCharacterPromptLimit] = useState(12000);
  const [customCharacterDifficulties, setCustomCharacterDifficulties] = useState([]);
  const [customCharacterEditor, setCustomCharacterEditor] = useState(null);
  const [customCharacterSaving, setCustomCharacterSaving] = useState(false);
  const [pendingMission, setPendingMission] = useState(null); // Выбранная миссия ожидающая начала
  const [customUsername, setCustomUsername] = useState(''); // Введенный username
  const [customUserDescription, setCustomUserDescription] = useState(''); // Описание пользовательского персонажа
  const [rulesEditor, setRulesEditor] = useState(null);
  const [manualExit, setManualExit] = useState(false); // Флаг явного выхода из диалога
  const [confirmModal, setConfirmModal] = useState(null); // Модальное окно подтверждения (reset/undo)
  const [paymentItems, setPaymentItems] = useState([]); // Товары для оплаты голосами
  const [subscriptions, setSubscriptions] = useState([]); // Планы подписок
  const [referralInfo, setReferralInfo] = useState(null); // Реферальная информация
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(false); // Подтверждение удаления аккаунта
  const [accountDeleted, setAccountDeleted] = useState(false); // Аккаунт удалён — блокируем приложение
  const [termsAccepted, setTermsAccepted] = useState(() => {
    const saved = localStorage.getItem('eleonora_terms_accepted');
    return saved === 'true';
  });
  const [appId, setAppId] = useState(54472959);
  const [missionLoading, setMissionLoading] = useState(false);
  const [launchParams, setLaunchParams] = useState(null); // Launch params из VK для авторизации
  const [paymentSnackbar, setPaymentSnackbar] = useState(null); // Snackbar для успешной оплаты
  const [bonusSnackbar, setBonusSnackbar] = useState(null); // Snackbar для ежедневного бонуса

  const getLaunchParamsString = useCallback(() => {
    if (window.location.search && window.location.search.length > 1) {
      return window.location.search;
    }

    const hash = window.location.hash || '';
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      return hash.slice(queryIndex);
    }

    const hashParams = hash.startsWith('#') ? hash.slice(1) : hash;
    if (hashParams.includes('vk_') || hashParams.includes('sign=')) {
      return `?${hashParams}`;
    }

    return null;
  }, []);

  const getVkUserIdFromLaunchParams = useCallback((params) => {
    if (!params || params === '__test__') return null;
    const cleanParams = params.startsWith('?') ? params.slice(1) : params;
    const parsed = new URLSearchParams(cleanParams);
    const rawId = parsed.get('vk_user_id');
    const numericId = Number(rawId);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }, []);

  const isTouchInputDevice = useCallback(() => (
    window.matchMedia?.('(pointer: coarse)').matches || window.innerWidth <= 768
  ), []);

  const scrollChatToBottom = useCallback((behavior = 'smooth') => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
      });
    });
  }, []);

  const keepTextareaBottomVisible = useCallback((target) => {
    if (!target) return;
    window.requestAnimationFrame(() => {
      target.scrollTop = target.scrollHeight;
    });
  }, []);

  const messagesEndRef = useRef(null);
  const purchaseIntervalRef = useRef(null);
  // Keep one product request ID across a timeout/manual retry. Agent Platform
  // uses it as the durable turn and billing idempotency key.
  const pendingSendRef = useRef(null);
  const pendingControlRef = useRef({});

  const controlRequestId = (action, characterId, missionId, slotId) => {
    const scope = [action, characterId, missionId || '', slotId].join('\u001f');
    const pending = pendingControlRef.current[action];
    if (pending?.scope === scope) return pending.requestId;
    const requestId = `${action}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    pendingControlRef.current[action] = { scope, requestId };
    return requestId;
  };

  const clearControlRequest = (action) => {
    delete pendingControlRef.current[action];
  };

  const loadSharedWorldOverview = useCallback(async () => {
    if (!launchParams || !profile?.user_id) return null;
    setSharedWorldLoading(true);
    try {
      const data = await getSharedWorldStatus(launchParams);
      setSharedWorldOverview(data);
      setSharedWorldError(null);
      return data;
    } catch (err) {
      console.error('Error loading shared world:', err);
      const detail = err?.response?.data?.detail;
      setSharedWorldError(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Не удалось загрузить состояние общего мира.',
      );
      return null;
    } finally {
      setSharedWorldLoading(false);
    }
  }, [launchParams, profile?.user_id]);

  useEffect(() => {
    if (!launchParams || !profile?.user_id) return;
    loadSharedWorldOverview();
  }, [launchParams, profile?.user_id, loadSharedWorldOverview]);

  // Загрузка товаров для оплаты голосами и подписок
  useEffect(() => {
    const loadPaymentItems = async () => {
      try {
        const lp = launchParams || getLaunchParamsString();
        const data = await getPaymentItems(lp);
        setPaymentItems(data.items || []);
      } catch (err) {
        console.error('Error loading payment items:', err);
      }
    };
    const loadSubscriptions = async () => {
      try {
        const data = await getSubscriptions();
        setSubscriptions(data.plans || []);
      } catch (err) {
        console.error('Error loading subscriptions:', err);
      }
    };
    loadPaymentItems();
    loadSubscriptions();
  }, [launchParams, getLaunchParamsString]);

  // Показываем snackbar при начислении ежедневного бонуса
  useEffect(() => {
    if (profile?.bonus?.claimed && !bonusSnackbar) {
      setBonusSnackbar(
        <Snackbar
          onClose={() => setBonusSnackbar(null)}
          before={<span style={{ fontSize: 20 }}>🎁</span>}
        >
          Ежедневный бонус: +{profile.bonus.messages} сообщений
        </Snackbar>
      );
    }
  }, [profile?.bonus?.claimed, profile?.bonus?.messages]);
  useEffect(() => {
    const handleBridgeEvent = (event) => {
      const { type, data } = event.detail;
      if (type === 'VKWebAppShowOrderBoxResult') {
        console.log('✅ VKWebAppShowOrderBoxResult:', data);
        setPaymentSnackbar(
          <Snackbar
            onClose={() => setPaymentSnackbar(null)}
            before={<span style={{ fontSize: 20 }}>✅</span>}
          >
            Оплата завершена успешно
          </Snackbar>
        );
      } else if (type === 'VKWebAppShowOrderBoxFailed') {
        console.log('❌ VKWebAppShowOrderBoxFailed:', data);
      }
    };
    bridge.subscribe(handleBridgeEvent);
    return () => bridge.unsubscribe(handleBridgeEvent);
  }, []);

  // Инициализация VK Bridge
  useEffect(() => {
    const initApp = async () => {
      try {
        setPopout(<ScreenSpinner />);
        const lpFromUrl = getLaunchParamsString();

        // Пробуем определить, запущены ли мы в VK Mini App
        // через попытку инициализации VK Bridge
        let isVK = false;

        try {
          // Пробуем инициализировать VK Bridge
          await Promise.race([
            bridge.send('VKWebAppInit'),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('VK Bridge timeout')), 8000)
            )
          ]);
          // Если успешно - мы в VK
          isVK = true;
          console.log('✓ VK Bridge инициализирован - мы в VK Mini App');
        } catch (err) {
          isVK = !!lpFromUrl;
          console.log('✗ VK Bridge не доступен:', err.message);
          if (isVK) {
            console.warn('Продолжаем VK-авторизацию по launch params');
          }
        }

        if (isVK) {
          try {
            const lp = lpFromUrl;
            if (!lp) {
              throw new Error('Нет launch params VK для авторизации');
            }

            // Получаем информацию о пользователе
            let userInfo;
            try {
              userInfo = await Promise.race([
                bridge.send('VKWebAppGetUserInfo'),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('VK user info timeout')), 8000)
                )
              ]);
            } catch (userInfoError) {
              const fallbackVkUserId = getVkUserIdFromLaunchParams(lp);
              if (!fallbackVkUserId) {
                throw userInfoError;
              }
              console.warn('VK user info недоступен, используем vk_user_id из launch params');
              userInfo = {
                id: fallbackVkUserId,
                first_name: '',
                last_name: '',
                photo_100: ''
              };
            }
            setVkUserId(userInfo.id);
            setUser(userInfo);

            // Получаем настоящий access_token через VK Bridge
            let accessToken = 'dummy_token';
            try {
              // Используем VK_APP_ID из .env (подставляется при сборке)
              const vkAppIdStr = '__VK_APP_ID__';
              const vkAppId = vkAppIdStr !== '__VK_APP_ID__' ? parseInt(vkAppIdStr) : 54472959;

              console.log('📱 VK App ID для авторизации:', vkAppId);
              setAppId(vkAppId);

              const authTokenResult = await Promise.race([
                bridge.send('VKWebAppGetAuthToken', {
                  app_id: vkAppId,
                  scope: '' // Пустые права доступа, только идентификация
                }),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('VK Auth Token timeout')), 10000)
                )
              ]);

              console.log('🔑 Результат VKWebAppGetAuthToken:', authTokenResult);

              if (authTokenResult.access_token) {
                accessToken = authTokenResult.access_token;
                console.log('✓ Получен реальный VK access_token');
              } else {
                console.warn('⚠ VK не вернул access_token, используем dummy_token');
                console.warn('  Ответ VK:', authTokenResult);
              }
            } catch (tokenError) {
              console.error('❌ Ошибка получения access_token:', tokenError);
              console.warn('  Используем dummy_token для авторизации');
            }

            // Аутентификация на backend с реальным или dummy токеном
            setLaunchParams(lp);
            const authResult = await authUser(userInfo.id, accessToken, userInfo, lp);
            setProfile({ ...authResult.user, limits: Number(authResult.user.limits) });

            // Дополнительно синхронизируем баланс с сервером, чтобы избежать рассинхрона
            try {
              const profileData = await getProfile(userInfo.id, lp);
              setProfile({ ...profileData, limits: Number(profileData.limits) });
            } catch (profileErr) {
              console.warn('Failed to refresh profile after auth:', profileErr);
            }

            // Если доступ был отключён (удаление аккаунта), сбрасываем принятие условий
            if (authResult.user.access === false) {
              localStorage.removeItem('eleonora_terms_accepted');
              setTermsAccepted(false);
            }
          } catch (vkError) {
            console.error('VK auth error:', vkError);
            const backendMessage = vkError?.response?.data?.detail;
            setError(
              backendMessage
                ? `Не удалось авторизоваться: ${backendMessage}`
                : 'Не удалось авторизоваться. Попробуй обновить страницу.'
            );
            setPopout(null);
            return;
          }
        } else {
          // Локальный тест
          console.log('Запуск в тестовом режиме (не VK)');
          await initTestUser();
        }

        // Загружаем персонажей (новый формат с категориями)
        const charsData = await getCharacters();
        // Сохраняем иерархию категорий
        if (charsData.categories && Array.isArray(charsData.categories)) {
          setCharacters(charsData.categories);
        } else {
          // Fallback на старый формат
          setCharacters([{id: 'all', title: 'Все персонажи', characters: charsData}]);
        }

        // Загружаем миссии/приключения
        try {
          const missionsData = await fetch('/api/missions').then(r => r.json());
          if (missionsData.missions) {
            setMissions(missionsData.missions);
          }
        } catch (e) {
          console.error('Error loading missions:', e);
        }

        setPopout(null);
      } catch (err) {
        console.error('Initialization error:', err);
        setError('Не удалось инициализировать приложение: ' + err.message);
        setPopout(null);
      }
    };

    const initTestUser = async () => {
      const testUserId = 999999;
      setVkUserId(testUserId);
      setUser({
        id: testUserId,
        first_name: 'Тест',
        last_name: 'Пользователь',
        photo_100: 'https://vk.com/images/camera_100.png'
      });
      setLaunchParams('__test__');

      try {
        const authResult = await authUser(testUserId, 'dummy_token', {
          first_name: 'Тест',
          last_name: 'Пользователь',
          photo_100: 'https://vk.com/images/camera_100.png'
        }, '__test__');
        setProfile({ ...authResult.user, limits: Number(authResult.user.limits) });
      } catch (err) {
        console.warn('Test user auth failed, using mock profile:', err);
        setProfile({
          user_id: `vk_${testUserId}`,
          limits: 100,
          first_bonus: true,
          state: 'menu',
          access: true,
        });
      }
    };

    initApp();
  }, [getLaunchParamsString, getVkUserIdFromLaunchParams]);

  const lastMessageSignature = messages.length > 0
    ? `${messages.length}:${messages[messages.length - 1]?.role}:${messages[messages.length - 1]?.text?.length || 0}`
    : 'empty';

  // Прокрутка в чате при новых сообщениях, замене истории и появлении индикатора ответа
  useEffect(() => {
    if (activePanel !== 'chat') return;
    const timeout = window.setTimeout(() => scrollChatToBottom('smooth'), 60);
    return () => window.clearTimeout(timeout);
  }, [activePanel, lastMessageSignature, isLoading, scrollChatToBottom]);

  // Восстановление диалога при загрузке (если state = dialogue_XXX)
  useEffect(() => {
    const restoreDialogue = async () => {
      if (profile?.state && characters.length > 0 && activePanel === 'main' && !manualExit) {
        const state = profile.state;
        if (state.startsWith('dialogue_')) {
          const payload = state.replace('dialogue_', '');
          const slotMatch = payload.match(/^(.*)_slot_(\d+)$/);
          const characterId = slotMatch ? slotMatch[1] : payload;
          const slotId = slotMatch ? Number(slotMatch[2]) : 0;
          let character = findCharacterById(characterId);
          if (!character && characterId.startsWith('custom:')) {
            try {
              const customCharacter = await getCustomCharacter(
                launchParams,
                characterId,
              );
              character = customCharacterForDialogue(customCharacter);
            } catch (err) {
              console.warn('Could not restore custom character:', err);
            }
          }
          if (character && vkUserId) {
            // Автоматически открываем чат с историей
            await openChatWithHistory(character, null, slotId);
          }
        }
      }
    };

    restoreDialogue();
  }, [profile, characters, activePanel, manualExit, vkUserId]);

  // Функция для открытия чата с загрузкой истории
  const openChatWithHistory = async (character, missionNum = null, slotId = activeSlotId) => {
    if (!vkUserId || !character) return;

    setSelectedCharacter(character);
    const resolvedSlotId = missionNum ? 0 : Number(slotId || 0);
    setActiveSlotId(resolvedSlotId);
    setActivePanel('chat');
    setSharedWorldBinding(null);

    // Загружаем историю сообщений
    try {
      const history = await getChatHistory(vkUserId, character.id, launchParams, missionNum, resolvedSlotId);
      if (history.messages && history.messages.length > 0) {
        // Сохраняем role из ответа сервера, если она есть
        setMessages(history.messages.map(msg => ({
          role: msg.role,
          text: msg.text,
          characterName: msg.characterName
        })));
      } else {
        setMessages([]);
      }
      setSharedWorldBinding(history.shared_world_binding || null);
    } catch (err) {
      console.error('Error loading chat history:', err);
      setMessages([]);
      setSharedWorldBinding(null);
    }
  };

  const handleCharacterSelect = async (character) => {
    if (!vkUserId) return;

    setPopout(<ScreenSpinner />);

    try {
      const slotsData = await getCharacterSlots(launchParams, character.id);
      setPendingCharacter(character);
      setCharacterSlots(slotsData.slots || []);
      setCharacterSlotLimit(slotsData.limit || 2);
      setActiveSlotId(slotsData.active_slot_id || 0);
      setEditingSlotId(null);
      setActivePanel('slots');
    } catch (err) {
      console.error('Error loading character slots:', err);
      setError('Не удалось загрузить слоты истории. Попробуй ещё раз.');
    } finally {
      setPopout(null);
    }
  };

  const customCharacterForDialogue = (character) => ({
    id: character.character_key,
    name: character.display_name,
    description: `Свой персонаж · ${character.difficulty_label}`,
    isCustom: true,
    customCharacterId: character.character_id,
  });

  const loadCustomCharacters = async () => {
    const data = await getCustomCharacters(launchParams);
    setCustomCharacters(data.characters || []);
    setCustomCharacterLimit(data.limit || 2);
    setCustomCharacterPromptLimit(data.prompt_max_length || 12000);
    setCustomCharacterDifficulties(data.difficulties || []);
    return data;
  };

  const handleOpenCustomCharacters = async () => {
    setPopout(<ScreenSpinner />);
    try {
      await loadCustomCharacters();
      setCustomCharacterEditor(null);
      setActivePanel('customCharacters');
    } catch (err) {
      console.error('Error loading custom characters:', err);
      setError('Не удалось загрузить своих персонажей. Попробуй ещё раз.');
    } finally {
      setPopout(null);
    }
  };

  const handleCreateCustomCharacter = () => {
    if (customCharacters.length >= customCharacterLimit) return;
    setCustomCharacterEditor({
      characterId: null,
      displayName: `Персонаж ${customCharacters.length + 1}`,
      systemPrompt: '',
      difficultyKey: 'standard',
    });
    setActivePanel('customCharacterEditor');
  };

  const handleEditCustomCharacter = async (characterId) => {
    setPopout(<ScreenSpinner />);
    try {
      const character = await getCustomCharacter(launchParams, characterId);
      setCustomCharacterEditor({
        characterId: character.character_id,
        displayName: character.display_name || '',
        systemPrompt: character.system_prompt || '',
        difficultyKey: character.difficulty_key || 'standard',
      });
      setActivePanel('customCharacterEditor');
    } catch (err) {
      console.error('Error loading custom character:', err);
      setError('Не удалось открыть персонажа. Попробуй ещё раз.');
    } finally {
      setPopout(null);
    }
  };

  const handleSaveCustomCharacter = async () => {
    if (
      !customCharacterEditor?.displayName.trim()
      || !customCharacterEditor?.systemPrompt.trim()
      || customCharacterSaving
    ) return;
    setCustomCharacterSaving(true);
    setPopout(<ScreenSpinner />);
    try {
      const payload = {
        displayName: customCharacterEditor.displayName.trim(),
        systemPrompt: customCharacterEditor.systemPrompt.trim(),
        difficultyKey: customCharacterEditor.difficultyKey,
      };
      if (customCharacterEditor.characterId) {
        await updateCustomCharacter(
          launchParams,
          customCharacterEditor.characterId,
          payload,
        );
      } else {
        await createCustomCharacter(launchParams, payload);
      }
      await loadCustomCharacters();
      setCustomCharacterEditor(null);
      setActivePanel('customCharacters');
    } catch (err) {
      console.error('Error saving custom character:', err);
      setError(err.response?.data?.detail || 'Не удалось сохранить персонажа.');
    } finally {
      setCustomCharacterSaving(false);
      setPopout(null);
    }
  };

  const handleDeleteCustomCharacter = (character) => {
    setConfirmModal({
      type: 'delete_custom_character',
      title: 'Удалить персонажа?',
      message: `«${character.display_name}» исчезнет из списка и освободит слот. Истории этого персонажа больше не будут доступны.`,
      onConfirm: async () => {
        setPopout(<ScreenSpinner />);
        try {
          await deleteCustomCharacter(launchParams, character.character_id);
          await loadCustomCharacters();
          setConfirmModal(null);
        } catch (err) {
          console.error('Error deleting custom character:', err);
          setError(err.response?.data?.detail || 'Не удалось удалить персонажа.');
        } finally {
          setPopout(null);
        }
      },
    });
  };

  const characterDialogueState = (characterId, slotId) => (
    slotId > 0 ? `dialogue_${characterId}_slot_${slotId}` : `dialogue_${characterId}`
  );

  const refreshCharacterSlots = async (character = pendingCharacter) => {
    if (!character) return;
    const slotsData = await getCharacterSlots(launchParams, character.id);
    setCharacterSlots(slotsData.slots || []);
    setCharacterSlotLimit(slotsData.limit || 2);
  };

  const handleSlotEnter = async (slotId) => {
    if (!pendingCharacter || !vkUserId) return;
    setPopout(<ScreenSpinner />);
    try {
      await selectCharacterSlot(launchParams, pendingCharacter.id, slotId);
      const sessionCheck = await checkSession(
        launchParams, pendingCharacter.id, slotId
      );
      setActiveSlotId(slotId);
      if (
        sessionCheck.has_session
        && (sessionCheck.has_history || sessionCheck.username)
      ) {
        await updateUserState(
          launchParams,
          characterDialogueState(pendingCharacter.id, slotId)
        );
        setManualExit(false);
        await openChatWithHistory(pendingCharacter, null, slotId);
      } else {
        setCustomUsername(sessionCheck.username || '');
        setCustomUserDescription(sessionCheck.user_description || '');
        // The setup form is rendered on the main panel. Suppress automatic
        // dialogue restoration while that form is open.
        setManualExit(true);
        setUsernameModal('username_input');
        setActivePanel('main');
      }
    } catch (err) {
      console.error('Error opening character slot:', err);
      setError('Не удалось открыть слот. Попробуй ещё раз.');
    } finally {
      setPopout(null);
    }
  };

  const handleCreateSlot = async () => {
    if (!pendingCharacter) return;
    setPopout(<ScreenSpinner />);
    try {
      const created = await createCharacterSlot(launchParams, pendingCharacter.id);
      await refreshCharacterSlots();
      await handleSlotEnter(created.slot_id);
    } catch (err) {
      console.error('Error creating character slot:', err);
      setError(err.response?.data?.detail || 'Не удалось создать слот.');
    } finally {
      setPopout(null);
    }
  };

  const handleRenameSlot = async (slotId) => {
    if (!pendingCharacter || !slotTitleInput.trim()) return;
    setPopout(<ScreenSpinner />);
    try {
      await renameCharacterSlot(
        launchParams, pendingCharacter.id, slotId, slotTitleInput.trim()
      );
      setEditingSlotId(null);
      setSlotTitleInput('');
      await refreshCharacterSlots();
    } catch (err) {
      console.error('Error renaming character slot:', err);
      setError('Не удалось переименовать слот.');
    } finally {
      setPopout(null);
    }
  };

  const handleDeleteSlot = (slotId) => {
    if (!pendingCharacter || slotId === 0) return;
    setConfirmModal({
      type: 'delete_slot',
      title: 'Удалить слот?',
      message: 'История этого слота будет удалена без возможности восстановления.',
      onConfirm: async () => {
        setPopout(<ScreenSpinner />);
        try {
          await deleteCharacterSlot(launchParams, pendingCharacter.id, slotId);
          setConfirmModal(null);
          setActiveSlotId(0);
          await refreshCharacterSlots();
        } catch (err) {
          console.error('Error deleting character slot:', err);
          setError(err.response?.data?.detail || 'Не удалось удалить слот.');
        } finally {
          setPopout(null);
        }
      },
    });
  };

  const handleResetUsername = async () => {
    if (!pendingCharacter || !vkUserId) return;

    // Сбрасываем username на дефолтное значение
    setCustomUsername('Пользователь');
    setCustomUserDescription('');

    try {
      // Обновляем сессию с дефолтным именем
      await selectCharacter(launchParams, pendingCharacter.id, 'Пользователь', null, '', activeSlotId);
      await updateUserState(launchParams, characterDialogueState(pendingCharacter.id, activeSlotId));

      // Закрываем модальное окно и открываем чат с историей
      setManualExit(false);
      setUsernameModal(null);
      await openChatWithHistory(pendingCharacter, null, activeSlotId);
    } catch (err) {
      console.error('Error resetting username:', err);
      setError('Не удалось сбросить имя. Попробуйте еще раз.');
    }
  };

  const handleUsernameConfirm = async () => {
    if (!customUsername.trim() || !pendingCharacter || !vkUserId) return;

    setPopout(<ScreenSpinner />);

    try {
      // Отправляем запрос на создание сессии с кастомным username
      await selectCharacter(
        launchParams,
        pendingCharacter.id,
        customUsername.trim(),
        null,
        customUserDescription.trim(),
        activeSlotId
      );

      // Устанавливаем state dialogue для отслеживания активного диалога
      await updateUserState(launchParams, characterDialogueState(pendingCharacter.id, activeSlotId));

      // Сбрасываем флаг явного выхода, чтобы можно было возвращаться в этот диалог
      setManualExit(false);

      // Закрываем модальное окно и открываем чат с историей
      setUsernameModal(null);
      await openChatWithHistory(pendingCharacter, null, activeSlotId);

      // Обновляем профиль для актуальных лимитов
      const profileData = await getProfile(vkUserId, launchParams);
      setProfile(profileData);
    } catch (err) {
      console.error('Error selecting character:', err);
      setError('Не удалось создать сессию. Попробуйте еще раз.');
    } finally {
      setPopout(null);
    }
  };

  const handleUsernameCancel = () => {
    const returnPanel = pendingCharacter?.isCustom ? 'customCharacters' : 'main';
    setUsernameModal(null);
    setPendingCharacter(null);
    setCustomUsername('');
    setCustomUserDescription('');
    setActivePanel(returnPanel);
  };

  // Обработка оплаты через голоса ВКонтакте
  const handlePurchase = async ({ itemId, title, messages, isSubscription, expectedPlanId }) => {
    if (!vkUserId) {
      console.log('NO VK USER ID');
      return;
    }

    const prevLimits = profile?.limits || 0;
    const prevPlanId = profile?.subscription?.plan_id;
    if (purchaseIntervalRef.current) {
      clearInterval(purchaseIntervalRef.current);
      purchaseIntervalRef.current = null;
    }
    try {
      const result = await bridge.send('VKWebAppShowOrderBox', {
        type: 'item',
        item: itemId,
        app_id: appId,
      });
      console.log('VKWebAppShowOrderBox result:', result);
    } catch (err) {
      console.error('Purchase error:', err);
      const reason = err?.error_data?.error_reason || err?.error_reason || '';
      // На m.vk.com часто возникает Unknown error, но оплата при этом проходит
      const isUnknownError = reason === 'Unknown error' || reason.toLowerCase().includes('unknown');
      const isCancelled = reason.toLowerCase().includes('cancel') || reason.toLowerCase().includes('denied');
      if (!isUnknownError && !isCancelled) {
        setError('❌ Ошибка при оплате. Попробуйте ещё раз.');
        setTimeout(() => setError(null), 5000);
      }
    } finally {
      // На m.vk.com webhook может задержаться — делаем polling профиля
      let attempts = 0;
      const maxAttempts = 6;
      purchaseIntervalRef.current = setInterval(async () => {
        attempts++;
        try {
          const profileData = await getProfile(vkUserId, launchParams);
          setProfile(profileData);
          const subscriptionChanged = isSubscription && expectedPlanId && profileData.subscription?.plan_id === expectedPlanId && profileData.subscription?.plan_id !== prevPlanId;
          const limitsIncreased = !isSubscription && profileData.limits > prevLimits;
          if (subscriptionChanged || limitsIncreased) {
            setPaymentSnackbar(
              <Snackbar
                onClose={() => setPaymentSnackbar(null)}
                before={<span style={{ fontSize: 20 }}>✅</span>}
              >
                {isSubscription
                  ? `Подписка "${title}" активирована`
                  : `Оплата завершена успешно. Зачислено ${messages} сообщений.`}
              </Snackbar>
            );
            clearInterval(purchaseIntervalRef.current);
            purchaseIntervalRef.current = null;
            return;
          }
          if (attempts >= maxAttempts) {
            clearInterval(purchaseIntervalRef.current);
            purchaseIntervalRef.current = null;
          }
        } catch (e) {
          console.error('Error refreshing profile after purchase:', e);
          if (attempts >= maxAttempts) {
            clearInterval(purchaseIntervalRef.current);
            purchaseIntervalRef.current = null;
          }
        }
      }, 1500);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const toggleSubcategory = (subcategoryId) => {
    setExpandedSubcategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subcategoryId)) {
        newSet.delete(subcategoryId);
      } else {
        newSet.add(subcategoryId);
      }
      return newSet;
    });
  };

  // Функция для форматирования markdown-звёздочек в жирный текст
  const formatMarkdownBold = (text) => {
    if (!text) return '';
    const escaped = text.replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
    return escaped.replace(/\*([^*\n]+)\*/g, '<b>$1</b>');
  };

  // Функция для очистки текста сообщения от служебной информации
  const cleanMessageText = (text) => {
    if (!text) return '';

    // Обрезаем текст по паттерну |[Description (регистронезависимо)
    const lowerText = text.toLowerCase();
    const index = lowerText.indexOf('|[description');
    if (index !== -1) {
      text = text.substring(0, index).trim();
    }

    return text;
  };

  // Компонент для отображения персонажа с аватаркой
  const CharacterCell = ({ character }) => (
    <Cell
      key={character.id}
      before={<CharacterAvatar character={character} size={48} lazy />}
      onClick={() => handleCharacterSelect(character)}
      style={{
        background: 'var(--vkui--color_background_content)',
        borderRadius: 12,
        marginBottom: 8,
        border: '1px solid var(--vkui--color_separator_secondary)',
        padding: '12px 16px'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Text weight="semibold" style={{ fontSize: 16, whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {character.name}
        </Text>
        <Text style={{
          fontSize: 14,
          color: 'var(--vkui--color_text_secondary)',
          lineHeight: '1.4',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word',
          maxHeight: '2.8em'
        }}>
          {character.description}
        </Text>
      </div>
    </Cell>
  );

  // Функция для поиска персонажа по ID во всех категориях и подкатегориях
  const findCharacterById = (characterId) => {
    const customCharacter = customCharacters.find(
      (character) => character.character_key === characterId
    );
    if (customCharacter) return customCharacterForDialogue(customCharacter);
    for (const category of characters) {
      // Ищем в основных персонажах категории
      const char = category.characters?.find(c => c.id === characterId);
      if (char) return char;

      // Ищем в подкатегориях
      if (category.subcategories) {
        for (const sub of category.subcategories) {
          const subChar = sub.characters?.find(c => c.id === characterId);
          if (subChar) return subChar;
        }
      }
    }
    return null;
  };

  const sharedWorldMission = (entry, character) => ({
    id: entry.mission_id,
    agent_name: character.id,
    title: sharedWorldOverview?.world?.name || 'Общий мир',
    world_id: entry.world_id,
    event_id: entry.event_id || null,
    isSharedWorld: true,
  });

  const handleSharedWorldContinue = async () => {
    const binding = sharedWorldOverview?.binding;
    if (!binding || !vkUserId) return;
    const character = findCharacterById(binding.character_id) || {
      id: binding.character_id,
      name: 'Общий мир',
      description: 'Ведущий общего мира',
    };
    setCurrentMission(sharedWorldMission({
      world_id: binding.world_id,
      event_id: binding.event_id,
      mission_id: binding.mission_id,
    }, character));
    setGameMode('multiplayer');
    await openChatWithHistory(character, binding.mission_id, 0);
  };

  const handleSharedWorldEnter = async ({
    characterId,
    eventId,
    username,
    userDescription,
  }) => {
    if (!characterId || !vkUserId || sharedWorldActionLoading) return;
    const character = findCharacterById(characterId) || {
      id: characterId,
      name: 'Общий мир',
      description: 'Ведущий общего мира',
    };
    setSharedWorldActionLoading(true);
    setSharedWorldError(null);
    try {
      const result = await enterSharedWorld(
        launchParams,
        characterId,
        username,
        userDescription,
        eventId,
      );
      setSharedWorldOverview(result.world_status);
      setCurrentMission(sharedWorldMission(result.entry, character));
      setGameMode('multiplayer');
      await openChatWithHistory(character, result.entry.mission_id, 0);
    } catch (err) {
      console.error('Error entering shared world:', err);
      const detail = err?.response?.data?.detail;
      setSharedWorldError(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Не удалось войти в общий мир.',
      );
    } finally {
      setSharedWorldActionLoading(false);
    }
  };

  const handleSharedWorldLeaveEvent = async (eventId) => {
    if (!eventId || sharedWorldActionLoading) return;
    setSharedWorldActionLoading(true);
    setSharedWorldError(null);
    try {
      const result = await leaveSharedWorldEvent(launchParams, eventId);
      setSharedWorldOverview(result.world_status);
    } catch (err) {
      console.error('Error leaving shared-world event:', err);
      const detail = err?.response?.data?.detail;
      setSharedWorldError(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Не удалось освободить место в событии.',
      );
    } finally {
      setSharedWorldActionLoading(false);
    }
  };

  const handleSharedWorldNotificationAck = async (notificationId) => {
    try {
      await acknowledgeSharedWorldNotification(launchParams, notificationId);
      setSharedWorldOverview((current) => ({
        ...current,
        notifications: (current?.notifications || []).filter(
          (item) => item.notification_id !== notificationId,
        ),
      }));
    } catch (err) {
      console.error('Error acknowledging shared-world notification:', err);
      setSharedWorldError('Не удалось отметить новость прочитанной.');
    }
  };

  const handleSendMessage = async () => {
    if (isLoading) return;
    if (!inputMessage.trim() || !selectedCharacter || !vkUserId) return;

    if ((profile?.limits || 0) <= 0) {
      setError('У вас закончились сообщения. Пополните баланс в магазине.');
      setTimeout(() => setError(null), 4000);
      return;
    }

    // Сохраняем сообщение для optimistic update и возможного отката
    const userMessage = inputMessage.trim();
    const sendScope = [
      selectedCharacter.id,
      currentMission?.id || '',
      currentMission ? 0 : activeSlotId,
      userMessage,
    ].join('\u001f');
    const pendingSend = pendingSendRef.current;
    const requestId = pendingSend?.scope === sendScope
      ? pendingSend.requestId
      : `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    pendingSendRef.current = { scope: sendScope, requestId };

    // Валидация: минимум 1 значимый символ и не только спецсимволы
    const hasMeaningfulChar = /[\p{L}\p{N}]/u.test(userMessage);
    if (!hasMeaningfulChar) {
      setError('Введи корректное сообщение');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setInputMessage('');
    setIsLoading(true);
    setError(null);

    // Оптимистично показываем сообщение пользователя
    setMessages(prev => [...prev, {
      role: 'user',
      text: userMessage,
      username: user?.first_name || 'Пользователь'
    }]);

    try {
      // Отправляем на backend (таймаут в api.js — 3 минуты)
      const response = await sendMessage(
        launchParams,
        selectedCharacter.id,
        userMessage,
        null,
        currentMission?.id,
        currentMission ? 0 : activeSlotId,
        requestId
      );

      // Перезагружаем историю с сервера, чтобы избежать дублирования
      // и гарантировать правильную роль сообщений
      const history = await getChatHistory(
        vkUserId,
        selectedCharacter.id,
        launchParams,
        currentMission?.id,
        currentMission ? 0 : activeSlotId
      );
      if (history.messages) {
        setMessages(history.messages);
        setSharedWorldBinding(history.shared_world_binding || null);
      } else {
        // Fallback: добавляем ответ персонажа (не должно срабатывать)
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: response.response,
          characterName: selectedCharacter.name
        }]);
      }

      // Обновляем лимиты
      setProfile(prev => ({ ...prev, limits: response.limits_remaining }));

      // Обновляем профиль с сервера для гарантии актуальности баланса
      const profileData = await getProfile(vkUserId, launchParams);
      setProfile(profileData);
      pendingSendRef.current = null;
    } catch (err) {
      console.error('Send message error:', err);
      const detail = err.response?.data?.detail;
      const errorCode = detail?.error || err.response?.data?.error;
      // The server history is authoritative. Remove this attempt's optimistic
      // copy so retrying the same durable request never duplicates it locally.
      setMessages(prev => {
        const index = [...prev].reverse().findIndex(
          msg => msg.role === 'user' && msg.text === userMessage
        );
        if (index < 0) return prev;
        const actualIndex = prev.length - 1 - index;
        return prev.filter((_msg, itemIndex) => itemIndex !== actualIndex);
      });
      setInputMessage(current => current || userMessage);
      if (['LIMIT_EXCEEDED', 'REQUEST_REJECTED', 'REQUEST_ID_CONFLICT'].includes(errorCode)) {
        pendingSendRef.current = null;
      }
      if (err.message === 'TIMEOUT' || err.code === 'ECONNABORTED') {
        setError('Сервер долго не отвечает. Повтори отправку: ход и списание не продублируются.');
      } else if (errorCode === 'LIMIT_EXCEEDED') {
        setError('У тебя закончились сообщения. Пополни баланс.');
      } else {
        setError(
          (typeof detail === 'object' && detail?.message)
          || (typeof detail === 'string' && detail)
          || 'Ошибка при отправке сообщения. Попробуй ещё раз.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = async () => {
    const returningFromSharedWorld = currentMission?.isSharedWorld;
    // Устанавливаем флаг явного выхода, чтобы useEffect не вернул нас обратно
    setManualExit(true);

    // Сбрасываем state в menu при выходе из диалога
    if (vkUserId) {
      try {
        await updateUserState(launchParams, 'menu');
        // Обновляем локальный profile, чтобы state совпадал с сервером
        setProfile(prev => ({ ...prev, state: 'menu' }));
      } catch (err) {
        console.error('Error updating state:', err);
      }
    }

    setActivePanel('main');
    setSelectedCharacter(null);
    setPendingCharacter(null);
    setCharacterSlots([]);
    setActiveSlotId(0);
    setCurrentMission(null); // Сбрасываем текущую миссию
    setMessages([]);
    setSharedWorldBinding(null);
    setError(null);
    if (returningFromSharedWorld) {
      await loadSharedWorldOverview();
    }
  };

  // Функция сброса диалога (с подтверждением)
  const openRulesEditor = async () => {
    if (!selectedCharacter || isLoading) return;
    const editor = {
      target: { launch_params: launchParams, character_id: selectedCharacter.id,
        mission_num: currentMission?.id || null, slot_id: currentMission ? 0 : activeSlotId },
      draft: '', loading: true, saving: false, error: null,
    };
    setRulesEditor(editor);
    setActivePanel('roleplay_rules');
    try {
      const result = await getRoleplayRules(editor.target);
      setRulesEditor((current) => current?.target === editor.target ? {
        ...current, draft: result.roleplay_rules || '', loading: false,
        error: result.busy ? 'Сейчас бот готовит ответ. Сохраните правила после его ответа.' : null,
      } : current);
    } catch (err) {
      setRulesEditor((current) => current?.target === editor.target ? {
        ...current, loading: false, loadFailed: true,
        error: typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Не удалось загрузить правила. Вернитесь в чат и повторите.',
      } : current);
    }
  };

  const handleSaveRules = async () => {
    if (!rulesEditor || rulesEditor.loading || rulesEditor.saving || rulesEditor.loadFailed) return;
    if ([...rulesEditor.draft].length > 4000) {
      setRulesEditor({ ...rulesEditor, error: 'Максимум 4000 символов. Сократите текст.' });
      return;
    }
    const editor = rulesEditor;
    setRulesEditor({ ...editor, saving: true, error: null });
    try {
      await saveRoleplayRules(editor.target, editor.draft);
      setRulesEditor(null);
      setActivePanel('chat');
    } catch (err) {
      setRulesEditor((current) => current?.target === editor.target ? {
        ...current, saving: false,
        error: typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Не удалось сохранить правила. Попробуйте ещё раз.',
      } : current);
    }
  };

  const handleResetDialog = () => {
    // Если диалог пустой — сбрасываем серверную сессию и показываем ввод имени
    if (messages.length === 0 && !sharedWorldBinding?.active) {
      if (!vkUserId || !selectedCharacter) return;
      setPopout(<ScreenSpinner />);
      const resetRequestId = controlRequestId(
        'reset', selectedCharacter.id, currentMission?.id || null,
        currentMission ? 0 : activeSlotId,
      );
      resetDialog(
        launchParams,
        selectedCharacter.id,
        currentMission?.id || null,
        currentMission ? 0 : activeSlotId,
        resetRequestId,
      )
        .then(() => {
          clearControlRequest('reset');
          setMessages([]);
          setSharedWorldBinding(null);
          setError(null);
          setCustomUsername('');
          setCustomUserDescription('');
          setPendingCharacter(selectedCharacter);
          setUsernameModal('username_input');
          setProfile(prev => ({ ...prev, state: 'menu' }));
          setActivePanel('main');
        })
        .catch((err) => {
          console.error('Error resetting empty dialog:', err);
          const detail = err?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : detail?.message || 'Не удалось сбросить диалог');
        })
        .finally(() => {
          setPopout(null);
        });
      return;
    }

    if (currentMission?.isSharedWorld) {
      setConfirmModal({
        type: 'reset_shared_world',
        title: 'Покинуть общий мир?',
        message: 'Личный диалог будет удалён. Изменения, которые уже стали частью общего мира, сохранятся для остальных игроков.',
        onConfirm: async () => {
          if (!vkUserId || !selectedCharacter) return;
          setPopout(<ScreenSpinner />);
          try {
            const resetRequestId = controlRequestId(
              'reset', selectedCharacter.id, currentMission.id, 0,
            );
            await resetDialog(
              launchParams,
              selectedCharacter.id,
              currentMission.id,
              0,
              resetRequestId,
            );
            clearControlRequest('reset');
            setMessages([]);
            setSharedWorldBinding(null);
            setCurrentMission(null);
            setConfirmModal(null);
            setActivePanel('main');
            setGameMode('multiplayer');
            await updateUserState(launchParams, 'menu');
            setProfile((current) => ({ ...current, state: 'menu' }));
            await loadSharedWorldOverview();
          } catch (err) {
            console.error('Error leaving shared world:', err);
            const detail = err?.response?.data?.detail;
            setError(
              typeof detail === 'string'
                ? detail
                : detail?.message || 'Не удалось покинуть общий мир',
            );
          } finally {
            setPopout(null);
            setConfirmModal(null);
          }
        },
      });
      return;
    }

    const isMission = !!currentMission;

    // Для миссий — сначала показываем подтверждение, потом описание
    if (isMission && currentMission) {
      const agentChar = findCharacterById(currentMission.agent_name);
      if (!agentChar) {
        setError('Персонаж для этой миссии временно недоступен');
        return;
      }

      setConfirmModal({
        type: 'reset',
        title: 'Начать приключение заново?',
        message: sharedWorldBinding?.active
          ? 'Личный диалог будет удалён, а персонаж покинет общий мир. Уже совершённые изменения мира сохранятся.'
          : 'Вся история приключения будет удалена. Ты сможешь начать сначала.',
        onConfirm: async () => {
          setPopout(<ScreenSpinner />);
          try {
            const resetRequestId = controlRequestId(
              'reset', selectedCharacter?.id, currentMission.id, 0,
            );
            await resetDialog(
              launchParams, selectedCharacter?.id, currentMission.id, 0,
              resetRequestId,
            );
            clearControlRequest('reset');
            setMessages([]);
            setSharedWorldBinding(null);
            setConfirmModal(null);
            // Сбрасываем state в menu, чтобы useEffect не вернул в диалог
            await updateUserState(launchParams, 'menu');
            setProfile(prev => ({ ...prev, state: 'menu' }));
            // Обновляем профиль после сброса диалога
            const profileData = await getProfile(vkUserId, launchParams);
            setProfile(profileData);
            // Возвращаемся на главную панель и показываем модальное окно с описанием миссии
            setActivePanel('main');
            setPendingMission({
              ...currentMission,
              agentCharacter: agentChar
            });
            setMissionModal('mission_start');
          } catch (err) {
            console.error('Error clearing mission:', err);
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : detail?.message || 'Не удалось очистить приключение');
          } finally {
            setPopout(null);
          }
        }
      });
      return;
    }

    // Для обычных диалогов — показываем подтверждение
    setConfirmModal({
      type: 'reset',
      title: 'Начать заново?',
      message: sharedWorldBinding?.active
        ? 'Личный диалог будет удалён, а персонаж покинет общий мир. Уже совершённые изменения мира сохранятся.'
        : 'Вся история сообщений будет удалена, а имя сброшено. Ты сможешь задать новое имя.',
      onConfirm: async () => {
        if (!vkUserId || !selectedCharacter) return;
        setPopout(<ScreenSpinner />);
        try {
          const resetRequestId = controlRequestId(
            'reset', selectedCharacter.id, null, activeSlotId,
          );
          await resetDialog(
            launchParams, selectedCharacter.id, null, activeSlotId,
            resetRequestId,
          );
          clearControlRequest('reset');
          setMessages([]);
          setSharedWorldBinding(null);
          setError(null);
          // Для обычных диалогов — показываем окно ввода имени
          setCustomUsername('');
          setCustomUserDescription('');
          setPendingCharacter(selectedCharacter);
          setUsernameModal('username_input');
          // Сбрасываем state в menu, чтобы useEffect не вернул в диалог
          setProfile(prev => ({ ...prev, state: 'menu' }));
          setActivePanel('main');
        } catch (err) {
          console.error('Error resetting dialog:', err);
          const detail = err?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : detail?.message || 'Не удалось очистить диалог');
        } finally {
          setPopout(null);
          setConfirmModal(null);
        }
      }
    });
  };

  // Функция удаления последних 2 реплик (с подтверждением)
  const handleUndoLast = async () => {
    // Требуется минимум 2 сообщения (вопрос + ответ) для удаления
    const hasSeededMissionIntro = currentMission && !currentMission.isSharedWorld;
    if ((hasSeededMissionIntro ? messages.length <= 2 : messages.length < 2) || isLoading) return;

    // Синхронизируемся с сервером, чтобы не удалить оплаченную историю,
    // если последнее сообщение не дошло (например, из-за пустого баланса)
    try {
      const history = await getChatHistory(
        vkUserId,
        selectedCharacter.id,
        launchParams,
        currentMission?.id,
        currentMission ? 0 : activeSlotId
      );
      const serverMessages = history.messages || [];
      setMessages(serverMessages);
      setSharedWorldBinding(history.shared_world_binding || null);
      if (hasSeededMissionIntro ? serverMessages.length <= 2 : serverMessages.length < 2) {
        // A previously timed-out undo may already have committed. Do not keep
        // its idempotency key and accidentally reuse it for a future turn.
        clearControlRequest('undo');
        return;
      }
    } catch (err) {
      console.error('Error syncing history before undo:', err);
      return;
    }

    setConfirmModal({
      type: 'undo',
      title: 'Удалить последнее сообщение?',
      message: 'Будет удалён твой последний вопрос и ответ персонажа. Продолжить?',
      onConfirm: async () => {
        if (!vkUserId || !selectedCharacter) return;
        setPopout(<ScreenSpinner />);
        try {
          const undoRequestId = controlRequestId(
            'undo', selectedCharacter.id, currentMission?.id || null,
            currentMission ? 0 : activeSlotId,
          );
          const result = await undoLast(
            launchParams,
            selectedCharacter.id,
            currentMission?.id,
            currentMission ? 0 : activeSlotId,
            undoRequestId,
          );
          if (result.status === 'success') {
            clearControlRequest('undo');
            // Обновляем список сообщений
            const history = await getChatHistory(
              vkUserId,
              selectedCharacter.id,
              launchParams,
              currentMission?.id,
              currentMission ? 0 : activeSlotId
            );
            setMessages(history.messages || []);
            setSharedWorldBinding(history.shared_world_binding || null);
            setError(null);
          } else {
            setError(result.message || 'Недостаточно сообщений для удаления');
          }
        } catch (err) {
          console.error('Error undoing last message:', err);
          const detail = err?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : detail?.message || 'Не удалось удалить сообщение');
        } finally {
          setPopout(null);
          setConfirmModal(null);
        }
      }
    });
  };

  // Функция отправки жалобы (открывает сообщения сообщества)
  const handleReport = () => {
    console.log('Opening report link...');
    // VKWebAppOpenLink часто блокирует внутренние vk-ссылки на мобильных,
    // используем window.open напрямую
    window.open('https://vk.com/write-237144818', '_blank');
  };

  // Принятие условий (первый вход или восстановление после удаления)
  const handleAcceptTerms = async () => {
    localStorage.setItem('eleonora_terms_accepted', 'true');
    setTermsAccepted(true);
    if (launchParams && launchParams !== '__test__') {
      setPopout(<ScreenSpinner />);
      try {
        await confirmAccess(launchParams);
        const profileData = await getProfile(vkUserId, launchParams);
        setProfile(profileData);
        setPopout(null);
      } catch (err) {
        console.warn('confirmAccess error:', err);
        setPopout(null);
        setError('Не удалось восстановить доступ. Попробуйте позже.');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const handleRequestNotifications = async () => {
    if (!vkUserId) return;
    try {
      const result = await bridge.send('VKWebAppAllowNotifications', {});
      if (result.result) {
        const backendResult = await enableNotifications(launchParams);
        setProfile(prev => ({
          ...prev,
          notifications_enabled: true,
          limits: (prev?.limits || 0) + (backendResult.bonus_messages || 0),
        }));
        if (backendResult.bonus_granted) {
          setError(`🔔 Уведомления включены. Вам начислено +${backendResult.bonus_messages} сообщений!`);
        } else {
          setError('🔔 Уведомления включены');
        }
      } else {
        setError('Уведомления не разрешены');
      }
    } catch (err) {
      console.error('Notification permission error:', err);
      const reason = err?.error_data?.error_reason || err?.error_reason || '';
      if (reason === 'Requests limit reached') {
        setError('Вы уже получали запрос на уведомления. Разрешить их можно в настройках ВКонтакте.');
      } else {
        setError('Не удалось запросить уведомления');
      }
    }
    setTimeout(() => setError(null), 4000);
  };

  // Открытие реферальной панели
  const handleOpenReferral = async () => {
    if (!vkUserId || !launchParams) return;
    setPopout(<ScreenSpinner />);
    try {
      const info = await getReferralInfo(vkUserId, launchParams);
      setReferralInfo(info);
    } catch (err) {
      console.error('Failed to load referral info:', err);
      const backendMessage = err?.response?.data?.detail;
      setReferralInfo({
        ok: false,
        code: profile?.referral?.code || null,
        error: backendMessage || 'Не удалось загрузить статистику',
      });
    } finally {
      setPopout(null);
      setActivePanel('referral');
    }
  };

  // Поделиться реферальной ссылкой
  const handleShareReferral = async () => {
    if (!referralInfo?.code || !launchParams) return;
    const link = referralInfo?.link || `https://vk.com/app${appId}#ref_${referralInfo.code}`;
    try {
      await bridge.send('VKWebAppShare', { link });
      await shareReferral(launchParams);
    } catch (err) {
      console.error('Share referral error:', err);
      // Fallback для desktop_web: копируем ссылку в буфер обмена
      try {
        await navigator.clipboard.writeText(link);
        setError('Ссылка скопирована в буфер обмена');
      } catch (clipboardErr) {
        setError('Не удалось поделиться ссылкой');
      }
      setTimeout(() => setError(null), 3000);
    }
  };

  // Получение ежедневного бонуса (как в Telegram)
  const handleClaimDailyBonus = async () => {
    if (!vkUserId || !launchParams) return;
    setPopout(<ScreenSpinner />);
    try {
      const result = await claimDailyBonus(launchParams);
      const profileData = await getProfile(vkUserId, launchParams);
      setProfile(profileData);
      setPopout(null);
      if (result.claimed) {
        setBonusSnackbar(
          <Snackbar
            onClose={() => setBonusSnackbar(null)}
            before={<span style={{ fontSize: 20 }}>🎁</span>}
          >
            Ежедневный бонус: +{result.messages} сообщений
          </Snackbar>
        );
      }
    } catch (err) {
      console.error('Claim daily bonus error:', err);
      setPopout(null);
      const msg = err.response?.data?.detail;
      if (msg === 'already_claimed' || (typeof msg === 'string' && msg.includes('already'))) {
        setError('Бонус уже получен сегодня. Заходи завтра!');
      } else {
        setError('Не удалось получить бонус. Попробуй позже.');
      }
      setTimeout(() => setError(null), 3000);
    }
  };

  // Принудительный выход из мини-приложения VK
  const forceCloseApp = async () => {
    // 1. Пробуем нативный метод VK Bridge
    try {
      await bridge.send('VKWebAppClose', { status: 'success' });
      return;
    } catch (closeErr) {
      console.warn('VKWebAppClose failed:', closeErr);
    }

    // 2. Пробуем уйти из iframe на уровень родителя
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = 'https://vk.com/';
        return;
      }
    } catch (topErr) {
      console.warn('window.top redirect blocked:', topErr);
    }

    // 3. Последний fallback — перезагрузить текущее окно/iframe
    window.location.href = 'https://vk.com/';
  };

  // Удаление аккаунта
  const handleDeleteAccount = async () => {
    if (!vkUserId) return;
    setPopout(<ScreenSpinner />);
    try {
      await deleteUser(launchParams);
      // Полностью очищаем локальное хранилище
      localStorage.clear();
      setDeleteAccountConfirm(false);
      setPopout(null);
      // Блокируем UI и показываем экран "Аккаунт удалён"
      setAccountDeleted(true);
      // Пытаемся закрыть приложение
      await forceCloseApp();
    } catch (err) {
      console.error('Delete account error:', err);
      setPopout(null);
      setError('Не удалось удалить аккаунт. Попробуйте позже.');
    }
  };

  if (!user || !profile) {
    return (
      <SplitLayout popout={popout}>
        <SplitCol>
          <Div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 16, padding: 24 }}>
            {error ? (
              <>
                <Text style={{ color: 'var(--vkui--color_text_negative)', textAlign: 'center' }}>
                  {error}
                </Text>
                <Button onClick={() => window.location.reload()}>
                  Обновить страницу
                </Button>
              </>
            ) : (
              <Spinner size="xl" />
            )}
          </Div>
        </SplitCol>
      </SplitLayout>
    );
  }

  return (
    <SplitLayout popout={popout}>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes typing-bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        .typing-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: var(--vkui--color_text_secondary);
          margin-right: 3px;
          animation: typing-bounce 1.4s infinite ease-in-out both;
        }
        .typing-dot:nth-child(1) { animation-delay: -0.32s; }
        .typing-dot:nth-child(2) { animation-delay: -0.16s; }
        .typing-dot:nth-child(3) { animation-delay: 0s; margin-right: 0; }
      ` }} />
      <SplitCol>
        {accountDeleted && (
          <Div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--vkui--color_background_content)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 64, marginBottom: 24 }}>🗑️</div>
            <Text weight="semibold" style={{ fontSize: 22, marginBottom: 12 }}>
              Аккаунт удалён
            </Text>
            <Text style={{ fontSize: 15, color: 'var(--vkui--color_text_secondary)', lineHeight: '1.4' }}>
              Все данные, диалоги и история безвозвратно удалены.
            </Text>
          </Div>
        )}
        {paymentSnackbar}
        {bonusSnackbar}

        {/* Модальное окно принятия условий */}
        {!termsAccepted && activePanel !== 'terms' && activePanel !== 'privacy' && (
          <Div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}>
            <Div style={{
              background: 'var(--vkui--color_background_content)',
              borderRadius: 16,
              padding: '32px 24px',
              maxWidth: 360,
              width: '100%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              textAlign: 'center'
            }}>
              <Text weight="semibold" style={{
                marginBottom: 12,
                fontSize: 22,
                color: 'var(--vkui--color_text_primary)'
              }}>
                Добро пожаловать!
              </Text>

              <Text style={{
                marginBottom: 16,
                color: 'var(--vkui--color_text_secondary)',
                fontSize: 15,
                lineHeight: '1.4'
              }}>
                Это платформа для общения с ИИ-персонажами и прохождения текстовых приключений.
              </Text>

              <Text style={{
                marginBottom: 24,
                color: 'var(--vkui--color_text_primary)',
                fontSize: 16,
                fontWeight: 'medium',
                lineHeight: '1.4'
              }}>
                Продолжая, вы принимаете наши условия.
              </Text>

              <Div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 0 }}>
                <Button
                  size="l"
                  mode="primary"
                  stretched
                  onClick={handleAcceptTerms}
                  style={{
                    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
                  }}
                >
                  Продолжить
                </Button>
              </Div>

              <Text style={{
                marginTop: 20,
                fontSize: 12,
                color: 'var(--vkui--color_text_secondary)',
                lineHeight: '1.3'
              }}>
                Нажимая "Продолжить", вы соглашаетесь с{' '}
                <span
                  onClick={() => setActivePanel('terms')}
                  style={{ color: 'var(--vkui--color_text_link)', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  пользовательским соглашением
                </span>{' '}
                и{' '}
                <span
                  onClick={() => setActivePanel('privacy')}
                  style={{ color: 'var(--vkui--color_text_link)', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  политикой конфиденциальности
                </span>
              </Text>
            </Div>
          </Div>
        )}

        <View activePanel={activePanel}>
          {/* Главная панель - список персонажей */}
          <Panel id="main">
            <PanelHeader>Eleonora Hostess</PanelHeader>

            <Group>
              <Banner
                before={<Avatar src={user.photo_100} />}
                title={`${user.first_name} ${user.last_name}`}
                subtitle={
                  profile?.subscription?.status === 'active'
                    ? `⭐ ${profile.subscription.plan_id.toUpperCase()} · ${profile.subscription.days_remaining} дн. · ${profile.limits} 💬`
                    : `Доступно сообщений: ${profile.limits}`
                }
              />
            </Group>

            {profile?.subscription?.status === 'active' && profile.subscription.days_remaining <= 3 && (
              <Group style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)' }}>
                <Div style={{ textAlign: 'center', padding: '12px 16px' }}>
                  <Text weight="semibold" style={{ fontSize: 15, color: 'white', display: 'block', marginBottom: 4 }}>
                    ⏰ Подписка закончится через {profile.subscription.days_remaining} {profile.subscription.days_remaining === 1 ? 'день' : 'дня'}
                  </Text>
                  <Button
                    size="s"
                    mode="overlay"
                    onClick={() => setActivePanel('shop')}
                  >
                    Продлить
                  </Button>
                </Div>
              </Group>
            )}

            {/* Описание приложения */}
            <Group>
              <Div>
                <Text style={{
                  fontSize: 16,
                  fontWeight: 'medium',
                  marginBottom: 12,
                  color: 'var(--vkui--color_text_primary)'
                }}>
                  👋 Добро пожаловать!
                </Text>
                <Text style={{
                  fontSize: 14,
                  lineHeight: '1.5',
                  color: 'var(--vkui--color_text_secondary)',
                  marginBottom: 16
                }}>
                  Это платформа, где ты можешь общаться с персонажами, которые помнят твои разговоры и обладают характером.
                  Или стать главным героем в приключениях, где твой выбор определяет сюжет.
                </Text>

                <Div style={{ padding: 0, marginBottom: 12 }}>
                  <Text style={{
                    fontSize: 14,
                    fontWeight: 'medium',
                    marginBottom: 8,
                    color: 'var(--vkui--color_text_primary)'
                  }}>
                    💬 Просто пообщаться
                  </Text>
                  <Text style={{
                    fontSize: 13,
                    lineHeight: '1.4',
                    color: 'var(--vkui--color_text_secondary)'
                  }}>
                    Найди собеседника по душе: от душевных бесед до интеллектуальных дебатов.
                    Персонажи адаптируются под твой стиль общения.
                  </Text>
                </Div>

                <Div style={{ padding: 0, marginBottom: 16 }}>
                  <Text style={{
                    fontSize: 14,
                    fontWeight: 'medium',
                    marginBottom: 8,
                    color: 'var(--vkui--color_text_primary)'
                  }}>
                    🎮 Начать приключение
                  </Text>
                  <Text style={{
                    fontSize: 13,
                    lineHeight: '1.4',
                    color: 'var(--vkui--color_text_secondary)'
                  }}>
                    Готовые сценарии-квесты по вселенным Гарри Поттера, Игры Престолов и другим.
                    Перепиши известные истории под себя.
                  </Text>
                </Div>
              </Div>
            </Group>

            {/* Дисклеймер о контенте */}
            <Group>
              <Banner
                before={<span style={{ fontSize: 28 }}>ℹ️</span>}
                title="О персонажах"
                subtitle={
                  <span>
                    Персонажи генерируют ответы с помощью ИИ и могут отвечать по-разному.
                    Если вы получили неприемлемый или оскорбительный ответ, пожалуйста, сообщите об этом через кнопку «Пожаловаться» в чате или напишите в сообщество.
                  </span>
                }
              />
            </Group>

            {/* Кнопка магазина */}
            <Group>
              <Cell
                before={<Icon28MarketOutline />}
                onClick={() => setActivePanel('shop')}
              >
                Магазин
              </Cell>
            </Group>

            {/* Кнопка подписок (без цен) */}
            <Group>
              <Cell
                before={<span style={{ fontSize: 24 }}>⭐</span>}
                onClick={() => setActivePanel('subscription')}
              >
                Подписки
              </Cell>
            </Group>

            {/* Кнопка ежедневного бонуса */}
            <Group>
              {(() => {
                const canClaim = profile?.daily_bonus?.can_claim && profile.daily_bonus.messages > 0;
                const nextClaim = profile?.daily_bonus?.next_claim;
                let nextText = '';
                if (!canClaim && nextClaim) {
                  const next = new Date(nextClaim);
                  const now = new Date();
                  const diffMs = Math.max(0, next - now);
                  const hours = Math.floor(diffMs / (1000 * 60 * 60));
                  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                  nextText = `следующий бонус через ${hours}ч ${minutes}м`;
                }
                return (
                  <Div style={{ paddingTop: 0 }}>
                    <Button
                      size="m"
                      mode="primary"
                      stretched
                      onClick={handleClaimDailyBonus}
                      disabled={!canClaim}
                      style={{
                        background: canClaim
                          ? 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)'
                          : 'linear-gradient(135deg, #b0b0b0 0%, #d0d0d0 100%)',
                      }}
                    >
                      {canClaim
                        ? `🎁 Получить ежедневный бонус +${profile.daily_bonus.messages} сообщений`
                        : `🎁 Бонус получен ${nextText ? `· ${nextText}` : ''}`}
                    </Button>
                  </Div>
                );
              })()}
            </Group>

            {/* Кнопка реферальной программы */}
            <Group>
              <Cell
                before={<span style={{ fontSize: 24 }}>👥</span>}
                onClick={handleOpenReferral}
              >
                Реферальная программа
              </Cell>
            </Group>

            {/* Кнопка уведомлений */}
            <Group>
              <Cell
                before={<span style={{ fontSize: 24 }}>{profile?.notifications_enabled ? '🔔' : '🔕'}</span>}
                onClick={handleRequestNotifications}
              >
                {profile?.notifications_enabled ? 'Уведомления включены' : 'Включить уведомления'}
              </Cell>
            </Group>

            {usernameModal === 'username_input' && pendingCharacter ? (
              // Форма ввода username
              <Group>
                <Div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Заголовок выбора персонажа */}
                  <Text style={{ marginBottom: 16, color: 'var(--vkui--color_text_secondary)', textAlign: 'center' }}>
                    Ты выбрал персонажа
                  </Text>
                  {/* Аватарка персонажа */}
                  <Div style={{ padding: 0, marginBottom: 16 }}>
                    <CharacterAvatar character={pendingCharacter} size={100} />
                  </Div>
                  <Text style={{ marginBottom: 24, fontSize: 20, fontWeight: 'bold' }}>
                    {pendingCharacter.name}
                  </Text>

                  {/* Описание персонажа */}
                  {pendingCharacter.description && (
                    <Div style={{
                      background: 'var(--vkui--color_background_secondary)',
                      borderRadius: 12,
                      padding: '12px 16px',
                      marginBottom: 24,
                      maxWidth: '100%'
                    }}>
                      <Text style={{
                        fontSize: 14,
                        color: 'var(--vkui--color_text_secondary)',
                        lineHeight: '1.4',
                        textAlign: 'center'
                      }}>
                        {pendingCharacter.description}
                      </Text>
                    </Div>
                  )}

                  <Div style={{ width: '100%' }}>
                    <Text style={{ marginBottom: 8, fontWeight: 'medium' }}>
                      Как к тебе обращаться?
                    </Text>
                    <Input
                      type="text"
                      value={customUsername}
                      onChange={(e) => setCustomUsername(e.target.value)}
                      placeholder="Введи своё имя"
                      maxLength={50}
                      style={{ marginBottom: 8 }}
                    />
                    <Text style={{ marginBottom: 12, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                      Это имя будет использоваться в диалоге. Ты можешь задать разные имена для разных персонажей.
                    </Text>
                    <Text style={{ marginBottom: 8, fontWeight: 'medium' }}>
                      Описание твоего персонажа
                    </Text>
                    <Textarea
                      value={customUserDescription}
                      onChange={(e) => setCustomUserDescription(e.target.value)}
                      placeholder="Привычки, внешность, характер — необязательно"
                      maxLength={500}
                      rows={3}
                      grow={false}
                      maxHeight={120}
                      style={{ marginBottom: 8 }}
                      slotProps={{
                        textArea: {
                          style: {
                            maxHeight: 120,
                            overflowY: 'auto',
                            resize: 'none',
                          }
                        }
                      }}
                    />
                    <Text style={{ marginBottom: 16, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                      Персонаж будет учитывать это описание в новом диалоге.
                    </Text>
                    <Div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 0 }}>
                      <Button
                        size="l"
                        mode="primary"
                        stretched
                        onClick={handleUsernameConfirm}
                        disabled={!customUsername.trim()}
                      >
                        Начать диалог
                      </Button>

                      <Div style={{ display: 'flex', gap: 8, padding: 0 }}>
                        <Button
                          size="m"
                          mode="secondary"
                          stretched
                          onClick={handleResetUsername}
                          style={{ flex: 1 }}
                        >
                          Начать без имени
                        </Button>
                      </Div>

                      <Button
                        size="m"
                        mode="outline"
                        stretched
                        onClick={handleUsernameCancel}
                        style={{
                          borderColor: 'var(--vkui--color_icon_secondary)',
                          color: 'var(--vkui--color_text_secondary)'
                        }}
                      >
                        ← Вернуться к выбору персонажа
                      </Button>
                    </Div>
                  </Div>
                </Div>
              </Group>
            ) : missionModal === 'mission_start' && pendingMission ? (
              // Модальное окно начала миссии
              <Group>
                <Div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Заголовок */}
                  <Text style={{ marginBottom: 16, color: 'var(--vkui--color_text_secondary)', textAlign: 'center' }}>
                    Ты выбрал приключение
                  </Text>

                  {/* Иконка миссии */}
                  <div style={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: 'var(--vkui--color_background_secondary)',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 48
                  }}>
                    🎲
                  </div>

                  {/* Название миссии */}
                  <Text style={{ marginBottom: 8, fontSize: 20, fontWeight: 'bold' }}>
                    {pendingMission.title}
                  </Text>

                  {/* Описание миссии */}
                  <Div style={{
                    background: 'var(--vkui--color_background_secondary)',
                    borderRadius: 12,
                    padding: '12px 16px',
                    marginBottom: 16,
                    maxWidth: '100%'
                  }}>
                    <Text style={{
                      fontSize: 14,
                      color: 'var(--vkui--color_text_secondary)',
                      lineHeight: '1.4',
                      textAlign: 'center'
                    }}>
                      {pendingMission.description}
                    </Text>
                  </Div>

                  {/* Информация о персонаже */}
                  <Text style={{
                    fontSize: 13,
                    color: 'var(--vkui--color_accent_violet)',
                    marginBottom: 24,
                    textAlign: 'center'
                  }}>
                    Ты играешь за: <b>{pendingMission.username_ru || pendingMission.username || 'Пользователь'}</b>
                  </Text>

                  <Div style={{ width: '100%' }}>
                    <Div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 0 }}>
                      <Button
                        size="l"
                        mode="primary"
                        stretched
                        onClick={async () => {
                          const mission = pendingMission;
                          const agentChar = pendingMission.agentCharacter;

                          if (!agentChar) {
                            setError('Персонаж для этой миссии временно недоступен');
                            setMissionModal(null);
                            setPendingMission(null);
                            return;
                          }

                          setMissionModal(null);
                          setPendingMission(null);

                          // Устанавливаем миссию и персонажа
                          setCurrentMission(mission);
                          setSelectedCharacter(agentChar);
                          setCustomUsername(mission.username_ru || mission.username || 'Пользователь');
                          setCustomUserDescription('');

                          if (missionLoading) return;
                          setMissionLoading(true);
                          try {
                            // Создаем сессию
                            await selectCharacter(launchParams, agentChar.id, mission.username_ru || mission.username || 'Пользователь', mission.id);

                            // Проверяем — есть ли уже история (повторный вход)
                            const history = await getChatHistory(vkUserId, agentChar.id, launchParams, mission.id);
                            if (history.messages && history.messages.length > 0) {
                              // Уже был диалог — показываем историю
                              setMessages(history.messages);
                              setSharedWorldBinding(history.shared_world_binding || null);
                              setActivePanel('chat');
                              setError(null);
                            } else {
                              // Новый диалог — инициализируем миссию с обоими сообщениями
                              setSharedWorldBinding(history.shared_world_binding || null);
                              setActivePanel('chat');
                              setError(null);

                              if (mission.msg_world && mission.msg_agent) {
                                try {
                                  const initData = await initMission(
                                    launchParams,
                                    agentChar.id,
                                    mission.id,
                                    mission.username_ru || mission.username || 'Пользователь',
                                    mission.msg_world,
                                    mission.msg_agent
                                  );
                                  setMessages(initData.messages);
                                } catch (err) {
                                  console.error('Error init mission:', err);
                                  setError('Не удалось инициализировать миссию');
                                }
                              }
                            }
                            // Сбрасываем фокус с поля ввода при первом открытии
                            if (document.activeElement && document.activeElement.tagName === 'TEXTAREA') {
                              document.activeElement.blur();
                            }
                          } catch (err) {
                            console.error('Error starting mission:', err);
                            setError('Не удалось начать миссию');
                          } finally {
                            setMissionLoading(false);
                          }
                        }}
                      >
                        Начать приключение
                      </Button>

                      <Button
                        size="m"
                        mode="outline"
                        stretched
                        onClick={() => {
                          setMissionModal(null);
                          setPendingMission(null);
                        }}
                        style={{
                          borderColor: 'var(--vkui--color_icon_secondary)',
                          color: 'var(--vkui--color_text_secondary)'
                        }}
                      >
                        ← Вернуться к выбору
                      </Button>
                    </Div>
                  </Div>
                </Div>
              </Group>
            ) : (
              <>
              <Group>
                <Div className="game-mode-switch" style={{
                  display: 'grid',
                  gridTemplateColumns: sharedWorldOverview?.enabled ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
                  gap: 8,
                  padding: '8px 0',
                  alignItems: 'stretch'
                }}>
                  <Button
                    className="game-mode-switch__button"
                    size="m"
                    mode={gameMode === 'characters' ? 'primary' : 'secondary'}
                    stretched
                    onClick={() => setGameMode('characters')}
                    before={<Icon28MessageOutline width={20} height={20} />}
                    style={{ minWidth: 0, fontSize: 13 }}
                  >
                    Диалоги
                  </Button>
                  <Button
                    className="game-mode-switch__button"
                    size="m"
                    mode={gameMode === 'adventures' ? 'primary' : 'secondary'}
                    stretched
                    onClick={() => setGameMode('adventures')}
                    before={<Icon28GameOutline width={20} height={20} />}
                    style={{ minWidth: 0, fontSize: 13 }}
                  >
                    Приключения
                  </Button>
                  {sharedWorldOverview?.enabled && (
                    <Button
                      className="game-mode-switch__button"
                      size="m"
                      mode={gameMode === 'multiplayer' ? 'primary' : 'secondary'}
                      stretched
                      onClick={() => {
                        setGameMode('multiplayer');
                        loadSharedWorldOverview();
                      }}
                      before={<Icon28GlobeOutline width={20} height={20} />}
                      style={{ minWidth: 0, fontSize: 13 }}
                    >
                      Общий мир
                    </Button>
                  )}
                </Div>
              </Group>

              {gameMode === 'characters' ? (
              <>
              <Group>
                <Cell
                  before={<span style={{ fontSize: 28 }}>🧩</span>}
                  subtitle="Создавай персонажей со своим характером и сложностью"
                  onClick={handleOpenCustomCharacters}
                >
                  Свои персонажи
                </Cell>
              </Group>
              <Group header={<Text weight="semibold" style={{ paddingLeft: 16, paddingRight: 16, marginTop: 12, marginBottom: 12 }}>Выбери категорию и персонажа</Text>}>
                {characters.length === 0 && <Div><Text>Нет персонажей</Text></Div>}
                {characters.map((category) => (
                  <Div key={category.id} style={{ marginBottom: 16, padding: 0 }}>
                    {/* Заголовок категории (кликабельный) */}
                    <Cell
                      onClick={() => toggleCategory(category.id)}
                      style={{
                        background: 'var(--vkui--color_background_secondary)',
                        borderRadius: 8,
                        marginBottom: 8
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Text weight="semibold" style={{ fontSize: 16 }}>
                          {category.title}
                        </Text>
                        <Text style={{
                          color: 'var(--vkui--color_text_secondary)',
                          fontSize: 14,
                          marginLeft: 8,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%'
                        }}>
                          {category.subcategories
                            ? `${category.subcategories.length} вселенных`
                            : `${category.characters?.length || 0} персонажей`
                          }
                          <span style={{ marginLeft: 4, flexShrink: 0, whiteSpace: 'nowrap' }}>{expandedCategories.has(category.id) ? '▼' : '▶'}</span>
                        </Text>
                      </div>
                    </Cell>

                    {/* Содержимое категории (если раскрыта) */}
                    {expandedCategories.has(category.id) && (
                      <Div style={{ paddingLeft: 16, paddingTop: 0 }}>
                        {category.subcategories ? (
                          // У категории есть подкатегории (двухуровневая иерархия)
                          category.subcategories.map((sub) => (
                            <Div key={sub.id} style={{ marginBottom: 12 }}>
                              {/* Заголовок подкатегории */}
                              <Cell
                                onClick={() => toggleSubcategory(sub.id)}
                                style={{
                                  background: 'var(--vkui--color_background_tertiary)',
                                  borderRadius: 8,
                                  marginBottom: 4
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                  <Text weight="medium" style={{ fontSize: 15 }}>
                                    {sub.title}
                                  </Text>
                                  <Text style={{
                                    color: 'var(--vkui--color_text_secondary)',
                                    fontSize: 13,
                                    marginLeft: 8
                                  }}>
                                    {sub.characters?.length || 0} {expandedSubcategories.has(sub.id) ? '▼' : '▶'}
                                  </Text>
                                </div>
                              </Cell>

                              {/* Персонажи подкатегории */}
                              {expandedSubcategories.has(sub.id) && (
                                <Div style={{ paddingLeft: 12, paddingTop: 4 }}>
                                  {sub.characters?.map((character) => (
                                    <CharacterCell key={character.id} character={character} />
                                  ))}
                                </Div>
                              )}
                            </Div>
                          ))
                        ) : (
                          // Прямые персонажи категории (одноуровневая)
                          category.characters?.map((character) => (
                            <CharacterCell key={character.id} character={character} />
                          ))
                        )}
                      </Div>
                    )}
                  </Div>
                ))}
              </Group>
              </>
            ) : gameMode === 'adventures' ? (
              <Group header={<Text weight="semibold" style={{ paddingLeft: 16, paddingRight: 16 }}>🎲 Выбери приключение</Text>}>
                {missions.length === 0 && <Div><Text>Нет доступных приключений</Text></Div>}
                {missions.map((mission) => (
                  <Div key={mission.id} style={{ marginBottom: 12 }}>
                    <Cell
                      onClick={async () => {
                        // Находим персонажа для миссии
                        const agentChar = findCharacterById(mission.agent_name);
                        if (agentChar) {
                          // Показываем модальное окно вместо немедленного старта
                          setPendingMission({
                            ...mission,
                            agentCharacter: agentChar
                          });
                          setMissionModal('mission_start');
                        } else {
                          setError('Персонаж для этой миссии временно недоступен');
                        }
                      }}
                      style={{
                        background: 'var(--vkui--color_background_secondary)',
                        borderRadius: 8,
                        minHeight: 60
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <span style={{ fontSize: 28 }}>🎲</span>
                        <div style={{ flex: 1 }}>
                          <Text weight="semibold" style={{ fontSize: 16, marginBottom: 4, display: 'block' }}>
                            {mission.title}
                          </Text>
                          <Text style={{ fontSize: 14, color: 'var(--vkui--color_text_secondary)', lineHeight: '1.3' }}>
                            {mission.description ? mission.description.charAt(0).toUpperCase() + mission.description.slice(1) : ''}
                          </Text>
                          <Text style={{ fontSize: 12, color: 'var(--vkui--color_accent_violet)', marginTop: 4, display: 'block' }}>
                            Ты играешь за: {mission.username_ru || mission.username}
                          </Text>
                        </div>
                      </div>
                    </Cell>
                  </Div>
                ))}
              </Group>
            ) : (
              <SharedWorldMode
                status={sharedWorldOverview}
                loading={sharedWorldLoading}
                actionLoading={sharedWorldActionLoading}
                error={sharedWorldError}
                onRefresh={loadSharedWorldOverview}
                onContinue={handleSharedWorldContinue}
                onEnter={handleSharedWorldEnter}
                onLeaveEvent={handleSharedWorldLeaveEvent}
                onAcknowledge={handleSharedWorldNotificationAck}
              />
            )}
            </>
          )}

          <Group>
            <Div>
              <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>
                История сообщений сохраняется между сессиями
              </Text>
            </Div>
          </Group>

          {/* Поддержка и правовая информация */}
          <Group>
            <Div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button
                size="m"
                mode="secondary"
                stretched
                Component="a"
                href="https://vk.me/club237144818"
                target="_blank"
                rel="noopener noreferrer"
              >
                Поддержка (сообщество ВК)
              </Button>
              <Div style={{ display: 'flex', gap: 8, padding: 0 }}>
                <Button
                  size="m"
                  mode="tertiary"
                  stretched
                  onClick={() => setActivePanel('terms')}
                >
                  Соглашение
                </Button>
                <Button
                  size="m"
                  mode="tertiary"
                  stretched
                  onClick={() => setActivePanel('privacy')}
                >
                  Конфиденциальность
                </Button>
              </Div>
              <Button
                size="m"
                mode="tertiary"
                appearance="negative"
                stretched
                onClick={() => setDeleteAccountConfirm(true)}
              >
                Удалить аккаунт и данные
              </Button>
            </Div>
          </Group>

          {/* Модальное окно подтверждения удаления аккаунта */}
          {deleteAccountConfirm && (
            <Div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px'
            }}>
              <Div style={{
                background: 'var(--vkui--color_background_content)',
                borderRadius: 16,
                padding: '24px',
                maxWidth: 320,
                width: '100%',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                textAlign: 'center'
              }}>
                <Text weight="semibold" style={{ marginBottom: 8, fontSize: 20 }}>
                  Удалить аккаунт?
                </Text>
                <Text style={{ marginBottom: 24, color: 'var(--vkui--color_text_secondary)', fontSize: 15, lineHeight: '1.4' }}>
                  Все твои данные, диалоги и история будут безвозвратно удалены.
                </Text>
                <Div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 0 }}>
                  <Button
                    size="l"
                    mode="primary"
                    stretched
                    appearance="negative"
                    onClick={handleDeleteAccount}
                  >
                    Да, удалить всё
                  </Button>
                  <Button
                    size="l"
                    mode="secondary"
                    stretched
                    onClick={() => setDeleteAccountConfirm(false)}
                  >
                    Отмена
                  </Button>
                </Div>
              </Div>
            </Div>
          )}
        </Panel>

          <Panel id="customCharacters">
            <PanelHeader
              before={
                <PanelHeaderBack
                  onClick={() => {
                    setCustomCharacterEditor(null);
                    setActivePanel('main');
                  }}
                />
              }
            >
              Свои персонажи
            </PanelHeader>

            <Group
              header={
                <Header>
                  Создано: {customCharacters.length} из {customCharacterLimit}
                </Header>
              }
            >
              {customCharacters.length === 0 ? (
                <Placeholder
                  icon={<span style={{ fontSize: 52 }}>🧩</span>}
                  header="Создай первого персонажа"
                >
                  Задай имя, подробное описание и подходящую сложность.
                </Placeholder>
              ) : (
                customCharacters.map((character) => (
                  <Cell
                    key={character.character_id}
                    before={<span style={{ fontSize: 28 }}>🧩</span>}
                    subtitle={character.difficulty_label}
                    onClick={() => handleCharacterSelect(customCharacterForDialogue(character))}
                    after={
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button
                          size="s"
                          mode="secondary"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEditCustomCharacter(character.character_id);
                          }}
                        >
                          Изменить
                        </Button>
                        <Button
                          size="s"
                          mode="tertiary"
                          appearance="negative"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDeleteCustomCharacter(character);
                          }}
                        >
                          Удалить
                        </Button>
                      </div>
                    }
                  >
                    {character.display_name}
                  </Cell>
                ))
              )}

              <Div>
                <Button
                  stretched
                  size="l"
                  mode="primary"
                  disabled={customCharacters.length >= customCharacterLimit}
                  onClick={handleCreateCustomCharacter}
                >
                  Создать персонажа
                </Button>
                {customCharacters.length >= customCharacterLimit && (
                  <Text style={{
                    marginTop: 10,
                    color: 'var(--vkui--color_text_secondary)',
                    fontSize: 13,
                    textAlign: 'center',
                  }}>
                    Лимит зависит от тарифа и увеличивается с подпиской.
                  </Text>
                )}
              </Div>
            </Group>
          </Panel>

          <Panel id="customCharacterEditor">
            <PanelHeader
              before={
                <PanelHeaderBack
                  onClick={() => {
                    setCustomCharacterEditor(null);
                    setActivePanel('customCharacters');
                  }}
                />
              }
            >
              {customCharacterEditor?.characterId ? 'Настройки персонажа' : 'Новый персонаж'}
            </PanelHeader>

            <Group>
              <Div>
                <Text weight="semibold" style={{ marginBottom: 8 }}>
                  Имя персонажа
                </Text>
                <Input
                  value={customCharacterEditor?.displayName || ''}
                  maxLength={60}
                  placeholder="Например, профессор Элиас"
                  onChange={(event) => setCustomCharacterEditor((current) => ({
                    ...current,
                    displayName: event.target.value,
                  }))}
                />
              </Div>

              <Div>
                <Text weight="semibold" style={{ marginBottom: 8 }}>
                  Сложность
                </Text>
                <select
                  value={customCharacterEditor?.difficultyKey || 'standard'}
                  onChange={(event) => setCustomCharacterEditor((current) => ({
                    ...current,
                    difficultyKey: event.target.value,
                  }))}
                  style={{
                    width: '100%',
                    minHeight: 44,
                    border: '1px solid var(--vkui--color_field_border_alpha)',
                    borderRadius: 8,
                    padding: '0 12px',
                    background: 'var(--vkui--color_field_background)',
                    color: 'var(--vkui--color_text_primary)',
                    fontSize: 16,
                  }}
                >
                  {customCharacterDifficulties.map((difficulty) => (
                    <option key={difficulty.key} value={difficulty.key}>
                      {difficulty.label}
                    </option>
                  ))}
                </select>
                <Text style={{
                  marginTop: 8,
                  color: 'var(--vkui--color_text_secondary)',
                  fontSize: 13,
                  lineHeight: '1.4',
                }}>
                  {CUSTOM_DIFFICULTY_DESCRIPTIONS[
                    customCharacterEditor?.difficultyKey || 'standard'
                  ]}
                </Text>
              </Div>

              <Div>
                <Text weight="semibold" style={{ marginBottom: 8 }}>
                  Описание персонажа
                </Text>
                <Textarea
                  value={customCharacterEditor?.systemPrompt || ''}
                  maxLength={customCharacterPromptLimit}
                  rows={10}
                  grow
                  placeholder="Опиши характер, роль, манеру речи, внешность, отношения и важные правила поведения."
                  onChange={(event) => setCustomCharacterEditor((current) => ({
                    ...current,
                    systemPrompt: event.target.value,
                  }))}
                />
                <Text style={{
                  marginTop: 8,
                  color: 'var(--vkui--color_text_secondary)',
                  fontSize: 12,
                  textAlign: 'right',
                }}>
                  {customCharacterEditor?.systemPrompt?.length || 0}/{customCharacterPromptLimit}
                </Text>
              </Div>

              <Div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Button
                  stretched
                  size="l"
                  mode="primary"
                  loading={customCharacterSaving}
                  disabled={
                    !customCharacterEditor?.displayName.trim()
                    || !customCharacterEditor?.systemPrompt.trim()
                  }
                  onClick={handleSaveCustomCharacter}
                >
                  Сохранить
                </Button>
                <Button
                  stretched
                  size="m"
                  mode="secondary"
                  onClick={() => {
                    setCustomCharacterEditor(null);
                    setActivePanel('customCharacters');
                  }}
                >
                  Отмена
                </Button>
              </Div>
            </Group>
          </Panel>

          <Panel id="slots">
            <PanelHeader
              before={
                <PanelHeaderBack
                  onClick={() => {
                    setActivePanel(pendingCharacter?.isCustom ? 'customCharacters' : 'main');
                    setPendingCharacter(null);
                    setCharacterSlots([]);
                    setEditingSlotId(null);
                  }}
                />
              }
            >
              Слоты истории
            </PanelHeader>

            <Group
              header={
                <Header>
                  {pendingCharacter?.name || 'Персонаж'} · {characterSlots.length} из {characterSlotLimit}
                </Header>
              }
            >
              {characterSlots.map((slot) => (
                <React.Fragment key={slot.slot_id}>
                  <Cell
                    subtitle={slot.has_history ? 'История сохранена' : 'Новая история'}
                    indicator={slot.is_active ? 'Выбран' : undefined}
                    onClick={() => handleSlotEnter(slot.slot_id)}
                    after={
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button
                          size="s"
                          mode="secondary"
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditingSlotId(slot.slot_id);
                            setSlotTitleInput(slot.title);
                          }}
                        >
                          Изменить
                        </Button>
                        {slot.slot_id > 0 && (
                          <Button
                            size="s"
                            mode="tertiary"
                            appearance="negative"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteSlot(slot.slot_id);
                            }}
                          >
                            Удалить
                          </Button>
                        )}
                      </div>
                    }
                  >
                    {slot.title}
                  </Cell>
                  {editingSlotId === slot.slot_id && (
                    <Div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Input
                        value={slotTitleInput}
                        maxLength={40}
                        onChange={(event) => setSlotTitleInput(event.target.value)}
                        style={{ flex: 1 }}
                      />
                      <Button
                        disabled={!slotTitleInput.trim()}
                        onClick={() => handleRenameSlot(slot.slot_id)}
                      >
                        Сохранить
                      </Button>
                    </Div>
                  )}
                </React.Fragment>
              ))}
              {characterSlots.length < characterSlotLimit && (
                <Div>
                  <Button stretched size="l" mode="secondary" onClick={handleCreateSlot}>
                    Создать новый слот
                  </Button>
                </Div>
              )}
            </Group>
          </Panel>

          <Panel id="roleplay_rules">
            <PanelHeader before={<PanelHeaderBack disabled={rulesEditor?.saving} onClick={() => {
              if (!rulesEditor?.saving) { setRulesEditor(null); setActivePanel('chat'); }
            }} />}>Правила игры</PanelHeader>
            <Group>
              <Div>
                <Text>Правила для текущего диалога и слота. Они действуют со следующего ответа и сохраняются при сбросе истории.</Text>
              </Div>
              {rulesEditor?.loading ? <Div><Spinner /></Div> : <Div>
                <Textarea
                  aria-label="Правила игры"
                  value={rulesEditor?.draft || ''}
                  onChange={(event) => setRulesEditor({ ...rulesEditor, draft: event.target.value, error: null })}
                  placeholder="Не описывай мои действия и мысли. Сохраняй характер персонажа. Оставайся в текущей сцене."
                  rows={10}
                  disabled={rulesEditor?.saving || rulesEditor?.loadFailed}
                />
                <Text style={{ display: 'block', textAlign: 'right', marginTop: 8,
                  color: [...(rulesEditor?.draft || '')].length > 4000 ? 'var(--vkui--color_text_negative)' : 'var(--vkui--color_text_secondary)' }}>
                  {[...(rulesEditor?.draft || '')].length}/4000
                </Text>
              </Div>}
              {rulesEditor?.error && <Div><Text role="alert" style={{ color: 'var(--vkui--color_text_negative)' }}>{rulesEditor.error}</Text></Div>}
              <Div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Button size="l" onClick={handleSaveRules}
                  disabled={!rulesEditor || rulesEditor.loading || rulesEditor.saving || rulesEditor.loadFailed || [...rulesEditor.draft].length > 4000}>
                  {rulesEditor?.saving ? 'Сохраняем…' : 'Сохранить'}
                </Button>
                <Button size="l" mode="secondary" disabled={rulesEditor?.loading || rulesEditor?.saving || rulesEditor?.loadFailed}
                  onClick={() => setRulesEditor({ ...rulesEditor, draft: '', error: null })}>Очистить текст</Button>
                <Button size="l" mode="tertiary" disabled={rulesEditor?.saving}
                  onClick={() => { setRulesEditor(null); setActivePanel('chat'); }}>Отмена</Button>
              </Div>
            </Group>
          </Panel>

          {/* Панель чата */}
          <Panel id="chat" className="eleonora-chat-panel">
            <PanelHeader
              before={<div style={{ marginLeft: 4 }}><PanelHeaderBack onClick={goBack} /></div>}
              after={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 12 }}>
                  {/* Баланс с иконкой */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'var(--vkui--color_background_secondary)',
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 500
                  }}>
                    <span style={{ fontSize: 16 }}>💬</span>
                    <span>{profile.limits}</span>
                  </div>
                  {/* Кнопка магазина */}
                  <Button
                    mode="primary"
                    size="s"
                    onClick={() => setActivePanel('shop')}
                    before={<span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>+</span>}
                  >
                    Пополнить
                  </Button>
                </div>
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {currentMission?.isSharedWorld && (
                  <Icon28GlobeOutline
                    width={20}
                    height={20}
                    style={{ color: 'var(--vkui--color_icon_accent)' }}
                  />
                )}
                {selectedCharacter && (
                  <CharacterAvatar character={selectedCharacter} size={28} />
                )}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{selectedCharacter?.name || 'Чат'}</span>
              </div>
            </PanelHeader>

            {error && (
              <Group>
                <Banner
                  before={<Icon28ErrorOutline style={{ color: 'var(--vkui--color_icon_negative)' }} />}
                  title="Ошибка"
                  subtitle={error}
                  actions={
                    <Button mode="tertiary" onClick={() => setError(null)}>
                      Закрыть
                    </Button>
                  }
                />
              </Group>
            )}

            <Group style={{ minHeight: 'calc(100vh - 240px)' }}>
              {messages.length === 0 ? (
                <Placeholder
                  icon={<Icon28UsersOutline width={56} height={56} />}
                  header={currentMission?.isSharedWorld ? 'Общий мир' : 'Начни диалог'}
                  stretched
                >
                  {currentMission?.isSharedWorld
                    ? 'Опиши первое действие персонажа'
                    : 'Напиши первое сообщение персонажу и начни общение'}
                </Placeholder>
              ) : (
                <Div style={{ padding: '8px 8px 4px' }}>
                  {messages.map((msg, idx) => (
                    <Div
                      key={idx}
                      style={{
                        display: 'flex',
                        flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        marginBottom: 8,
                        maxWidth: '100%'
                      }}
                    >
                      {/* Аватарка */}
                      <Div style={{
                        padding: 0,
                        marginRight: msg.role === 'user' ? 0 : 6,
                        marginLeft: msg.role === 'user' ? 6 : 0,
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        background: 'var(--vkui--color_background_secondary)'
                      }}>
                        {msg.role === 'user' ? (
                          <img
                            src={user?.photo_100}
                            width={28}
                            height={28}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                            onError={(event) => {
                              event.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <CharacterAvatar character={selectedCharacter} size={28} />
                        )}
                      </Div>

                      {/* Сообщение */}
                      <Div
                        style={{
                          background: msg.role === 'user'
                            ? 'var(--vkui--color_background_accent)'
                            : 'var(--vkui--color_background_secondary)',
                          color: 'var(--vkui--color_text_primary)',
                          borderRadius: 10,
                          maxWidth: '82%',
                          padding: '6px 10px',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                          position: 'static',
                          overflow: 'hidden'
                        }}
                      >
                        <Text
                          weight="2"
                          style={{
                            fontSize: 11,
                            marginBottom: 2,
                            color: msg.role === 'user'
                              ? 'var(--vkui--color_text_contrast)'
                              : 'var(--vkui--color_text_secondary)'
                          }}
                        >
                          {msg.role === 'user' ? 'Ты' : msg.characterName}
                        </Text>
                        <Text style={{
                          fontSize: 14,
                          lineHeight: '1.35',
                          color: msg.role === 'user'
                            ? 'var(--vkui--color_text_contrast)'
                            : 'var(--vkui--color_text_primary)',
                          wordBreak: 'break-word'
                        }}>
                          <span dangerouslySetInnerHTML={{ __html: formatMarkdownBold(cleanMessageText(msg.text)) }} />
                        </Text>
                      </Div>
                    </Div>
                  ))}
                  {isLoading && (
                    <Div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        marginBottom: 8,
                        maxWidth: '100%'
                      }}
                    >
                      <Div style={{
                        padding: 0,
                        marginRight: 6,
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        background: 'var(--vkui--color_background_secondary)'
                      }}>
                        <CharacterAvatar character={selectedCharacter} size={28} />
                      </Div>
                      <Div
                        style={{
                          background: 'var(--vkui--color_background_secondary)',
                          color: 'var(--vkui--color_text_primary)',
                          borderRadius: 10,
                          maxWidth: '82%',
                          padding: '6px 10px',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                          overflow: 'hidden'
                        }}
                      >
                        <Text
                          weight="2"
                          style={{
                            fontSize: 11,
                            marginBottom: 2,
                            color: 'var(--vkui--color_text_secondary)'
                          }}
                        >
                          {selectedCharacter?.name || 'Бот'}
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', height: 20 }}>
                          <span className="typing-dot" />
                          <span className="typing-dot" />
                          <span className="typing-dot" />
                        </div>
                      </Div>
                    </Div>
                  )}
                  <div ref={messagesEndRef} />
                </Div>
              )}
            </Group>

            {/* Кнопки управления диалогом + поле ввода — всё в одном sticky-контейнере */}
            <Group style={{
              position: 'sticky',
              bottom: 0,
              background: 'var(--vkui--color_background_content)',
              zIndex: 10,
              boxShadow: '0 -1px 0 var(--vkui--color_separator_secondary)'
            }}>
              <Div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '4px 8px calc(8px + env(safe-area-inset-bottom))' }}>
                <Button size="s" mode="tertiary" onClick={openRulesEditor} disabled={isLoading}>
                  Правила игры
                </Button>
                {/* Пожаловаться */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <a
                    href="https://vk.com/write-237144818"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--vkui--color_text_secondary)',
                      fontSize: 12,
                      minHeight: 28,
                      padding: '4px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      textDecoration: 'none',
                      borderRadius: 8,
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    Пожаловаться
                  </a>
                </div>
                {/* Удалить последнее + Очистить всё */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
                  {((currentMission && !currentMission.isSharedWorld)
                    ? messages.length > 2
                    : messages.length >= 2) && (
                    <>
                      <Button
                        size="s"
                        mode="tertiary"
                        appearance="neutral"
                        before={<Icon28ReplyOutline width={20} height={20} />}
                        onClick={handleUndoLast}
                        disabled={isLoading || ((currentMission && !currentMission.isSharedWorld)
                          ? messages.length <= 2
                          : messages.length < 2)}
                        style={{
                          color: isLoading ? 'var(--vkui--color_text_tertiary)' : 'var(--vkui--color_text_secondary)',
                          fontSize: 12
                        }}
                      >
                        Удалить последнее
                      </Button>
                      <div style={{
                        width: 1,
                        background: 'var(--vkui--color_separator_secondary)',
                        margin: '4px 0'
                      }} />
                    </>
                  )}
                  <Button
                    size="s"
                    mode="tertiary"
                    appearance="negative"
                    before={<Icon28DeleteOutline width={20} height={20} />}
                    onClick={handleResetDialog}
                    disabled={isLoading}
                    style={{
                      color: isLoading ? 'var(--vkui--color_text_tertiary)' : 'var(--vkui--color_text_negative)',
                      fontSize: 12
                    }}
                  >
                    {currentMission?.isSharedWorld ? 'Покинуть мир' : 'Очистить всё'}
                  </Button>
                </div>
                {/* Поле ввода */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 2 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 4 }}>
                    <Textarea
                      value={inputMessage}
                      onChange={(e) => {
                        setInputMessage(e.target.value);
                        keepTextareaBottomVisible(e.target);
                      }}
                      onFocus={() => window.setTimeout(() => scrollChatToBottom('smooth'), 250)}
                      placeholder="Введи сообщение..."
                      rows={2}
                      grow={false}
                      maxHeight={96}
                      style={{ width: '100%', maxHeight: 96 }}
                      slotProps={{
                        textArea: {
                          style: {
                            minHeight: 44,
                            maxHeight: 96,
                            overflowY: 'auto',
                            resize: 'none',
                            lineHeight: '20px',
                          }
                        }
                      }}
                      maxLength={8000}
                      disabled={isLoading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !isLoading && !isTouchInputDevice()) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    {(() => {
                      const MAX_MSG_LEN = 8000;
                      const WARN_THRESHOLD = Math.floor(MAX_MSG_LEN * 0.9); // 7200
                      const remaining = MAX_MSG_LEN - inputMessage.length;
                      const isWarning = inputMessage.length >= WARN_THRESHOLD;
                      return (
                        <Text style={{
                          fontSize: 12,
                          color: isWarning ? 'var(--vkui--color_text_negative)' : 'var(--vkui--color_text_secondary)',
                          textAlign: 'right',
                          fontWeight: isWarning ? 'medium' : 'regular'
                        }}>
                          {isWarning ? `Осталось ${remaining} символов` : `${inputMessage.length}/${MAX_MSG_LEN}`}
                        </Text>
                      );
                    })()}
                  </div>
                    <Button
                      size="l"
                      mode="primary"
                      aria-label="Отправить"
                      onClick={handleSendMessage}
                      disabled={isLoading || !inputMessage.trim()}
                      loading={isLoading}
                      style={{ width: 48, minWidth: 48, minHeight: 44, padding: 0 }}
                    >
                    <Icon28Send width={22} height={22} />
                    </Button>
                </div>
              </Div>
            </Group>

            {/* Модальное окно подтверждения - поверх всего с затемнением */}
            {confirmModal && (
              <Div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(4px)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
              }}>
                <Div style={{
                  background: 'var(--vkui--color_background_content)',
                  borderRadius: 16,
                  padding: '24px',
                  maxWidth: 320,
                  width: '100%',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  textAlign: 'center'
                }}>
                  {/* Иконка предупреждения */}
                  <Div style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: confirmModal.type === 'reset'
                      ? 'var(--vkui--color_background_negative)'
                      : 'var(--vkui--color_background_accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    {confirmModal.type === 'reset' ? (
                      <Icon28DeleteOutline width={32} height={32} style={{ color: 'white' }} />
                    ) : (
                      <Icon28ReplyOutline width={32} height={32} style={{ color: 'white' }} />
                    )}
                  </Div>

                  <Text weight="semibold" style={{
                    marginBottom: 8,
                    fontSize: 20,
                    color: 'var(--vkui--color_text_primary)'
                  }}>
                    {confirmModal.title}
                  </Text>

                  <Text style={{
                    marginBottom: 24,
                    color: 'var(--vkui--color_text_secondary)',
                    fontSize: 15,
                    lineHeight: '1.4'
                  }}>
                    {confirmModal.message}
                  </Text>

                  <Div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 0 }}>
                    <Button
                      size="l"
                      mode="primary"
                      stretched
                      appearance={confirmModal.type === 'reset' ? 'negative' : 'accent'}
                      onClick={confirmModal.onConfirm}
                    >
                      {confirmModal.type === 'reset' ? 'Да, начать заново' : 'Да, удалить'}
                    </Button>
                    <Button
                      size="l"
                      mode="secondary"
                      stretched
                      onClick={() => setConfirmModal(null)}
                    >
                      Отмена
                    </Button>
                  </Div>
                </Div>
              </Div>
            )}

          </Panel>

          {/* Панель магазина */}
          <Panel id="shop">
            <PanelHeader
              before={<div style={{ marginLeft: 4 }}><PanelHeaderBack onClick={() => selectedCharacter ? setActivePanel('chat') : setActivePanel('main')} /></div>}
            >
              💎 Магазин
            </PanelHeader>

            {/* Hero Section */}
            <Group style={{
              background: 'linear-gradient(135deg, var(--vkui--color_accent_violet) 0%, var(--vkui--color_accent_purple) 100%)',
              marginBottom: 8
            }}>
              <Div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>💎</div>
                <Text weight="semibold" style={{
                  fontSize: 20,
                  color: 'white',
                  marginBottom: 8,
                  display: 'block'
                }}>
                  Продолжи свое уникальное общение!
                </Text>
                <Text style={{
                  fontSize: 15,
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.4',
                  display: 'block'
                }}>
                  Ты уже начал(а) увлекательный диалог — теперь раскрой все грани персонажа глубже!
                  Приобретай пакеты сообщений и создавай по-настоящему незабываемые моменты.
                </Text>
              </Div>
            </Group>

            {/* First Purchase Bonus */}
            {profile && !profile.first_bonus && (
              <Group style={{
                background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                marginBottom: 8
              }}>
                <Div style={{ textAlign: 'center', padding: '16px' }}>
                  <Text weight="semibold" style={{
                    fontSize: 16,
                    color: 'white',
                    marginBottom: 4,
                    display: 'block'
                  }}>
                    🎁 ПЕРВАЯ ПОКУПКА? Получи ДВОЙНОЙ бонус сообщений! 🎁
                  </Text>
                </Div>
              </Group>
            )}

            <Group>
              <Div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'var(--vkui--color_background_secondary)',
                borderRadius: 12,
                margin: '8px 16px',
                padding: '16px'
              }}>
                <div>
                  <Text style={{
                    fontSize: 13,
                    color: 'var(--vkui--color_text_secondary)',
                    marginBottom: 4,
                    display: 'block'
                  }}>
                    Твой текущий баланс
                  </Text>
                  <Text weight="semibold" style={{
                    fontSize: 24,
                    color: 'var(--vkui--color_accent_violet)'
                  }}>
                    {profile?.limits || 0} 💬
                  </Text>
                </div>
                <div style={{ fontSize: 32 }}>💰</div>
              </Div>
            </Group>

            {/* Subscriptions */}
            <Group header={<Header mode="secondary">⭐ Подписки (30 дней)</Header>}>
              <Div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {subscriptions.length === 0 && (
                  <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)', textAlign: 'center' }}>
                    Подписки временно недоступны
                  </Text>
                )}
                {subscriptions.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={async () => {
                      if (!vkUserId) return;
                      handlePurchase({ itemId: `sub_${plan.id}`, title: plan.name, isSubscription: true, expectedPlanId: plan.id });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      borderRadius: 10,
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #4a90d9 0%, #5e4ad9 100%)',
                      color: 'white',
                      userSelect: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 26, lineHeight: 1 }}>⭐</span>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>{plan.name}</div>
                        <div style={{ fontSize: 13, opacity: 0.9 }}>
                          До {plan.messages_per_month} сообщ. за 30 дней — ежедневно +{plan.daily_bonus || Math.max(1, Math.floor(plan.messages_per_month / 30))} сообщ.
                        </div>
                        {plan.perks && plan.perks.length > 0 && (
                          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                            {plan.perks.slice(0, 2).join(' • ')}
                          </div>
                        )}
                      </div>
                    </div>
                    <span style={{
                      background: 'rgba(255,255,255,0.25)',
                      color: 'white',
                      fontSize: 13,
                      padding: '4px 8px',
                      borderRadius: 10,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                      {plan.price_vk_votes} голосов
                    </span>
                  </div>
                ))}
              </Div>
            </Group>

            {/* Tariffs */}
            <Group header={<Header mode="secondary">🔥 Выбери свой пакет</Header>}>
              <Div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {paymentItems.filter(item => !item.subscription_plan).length === 0 && (
                  <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)', textAlign: 'center' }}>
                    Пакеты временно недоступны
                  </Text>
                )}
                {paymentItems
                  .filter(item => !item.subscription_plan)
                  .map((item) => {
                    const hasSubDiscount = (item.discount_percent || 0) > 0;
                    const hasVolumeDiscount = (item.volume_discount || 0) > 0;
                    return (
                      <div
                        key={item.item_id}
                        onClick={async () => {
                          console.log('TARIFF CLICKED:', item);
                          if (!vkUserId) {
                            console.log('NO VK USER ID');
                            return;
                          }
                          handlePurchase({ itemId: item.item_id, title: item.title, messages: item.messages });
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          borderRadius: 10,
                          cursor: 'pointer',
                          background: hasSubDiscount
                            ? 'linear-gradient(135deg, var(--vkui--color_accent_violet) 0%, var(--vkui--color_accent_purple) 100%)'
                            : 'var(--vkui--color_background_secondary)',
                          color: hasSubDiscount ? 'white' : 'var(--vkui--color_text_primary)',
                          userSelect: 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: 26, lineHeight: 1 }}>{Array.from(item.title || '💎')[0]}</span>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>{item.title}</div>
                            <div style={{ fontSize: 13, opacity: hasSubDiscount ? 0.9 : 0.8 }}>
                              {item.messages} сообщ. — {item.price} голосов
                              {hasSubDiscount && item.original_price ? (
                                <span style={{ textDecoration: 'line-through', opacity: 0.7, marginLeft: 6 }}>
                                  {item.original_price}
                                </span>
                              ) : null}
                            </div>
                            {hasVolumeDiscount && (
                              <div style={{ fontSize: 11, opacity: hasSubDiscount ? 0.8 : 0.7, marginTop: 2 }}>
                                {hasSubDiscount
                                  ? `Скидка за объем −${item.volume_discount}% + доп. −${item.discount_percent}% по подписке`
                                  : `Скидка за объем −${item.volume_discount}%`}
                              </div>
                            )}
                          </div>
                        </div>
                        {(hasVolumeDiscount || hasSubDiscount) && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                            {hasVolumeDiscount && (
                              <span style={{
                                background: hasSubDiscount ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)',
                                color: hasSubDiscount ? 'white' : 'var(--vkui--color_text_primary)',
                                fontSize: 11,
                                padding: '3px 7px',
                                borderRadius: 8,
                                fontWeight: 700,
                              }}>
                                −{item.volume_discount}% за объем
                              </span>
                            )}
                            {hasSubDiscount && (
                              <span style={{
                                background: 'rgba(255,255,255,0.25)',
                                color: 'white',
                                fontSize: 12,
                                padding: '4px 8px',
                                borderRadius: 10,
                                fontWeight: 700,
                              }}>
                                −{item.discount_percent}% доп.
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </Div>
            </Group>

            {/* Benefits */}
            <Group header={<Header mode="secondary">🚀 Почему это выгодно?</Header>}>
              <Div>
                <div style={{
                  display: 'grid',
                  gap: 12,
                  marginBottom: 16
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px',
                    background: 'var(--vkui--color_background_secondary)',
                    borderRadius: 8
                  }}>
                    <span style={{ fontSize: 20 }}>🎯</span>
                    <div>
                      <Text weight="medium" style={{ fontSize: 14, display: 'block', marginBottom: 2 }}>
                        Первая покупка = двойное количество!
                      </Text>
                      <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                        Получи в 2 раза больше сообщений при первой покупке любого пакета
                      </Text>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px',
                    background: 'var(--vkui--color_background_secondary)',
                    borderRadius: 8
                  }}>
                    <span style={{ fontSize: 20 }}>🧠</span>
                    <div>
                      <Text weight="medium" style={{ fontSize: 14, display: 'block', marginBottom: 2 }}>
                        Персонажи становятся «умнее»
                      </Text>
                      <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                        В длинных диалогах AI лучше понимает контекст и твой стиль
                      </Text>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px',
                    background: 'var(--vkui--color_background_secondary)',
                    borderRadius: 8
                  }}>
                    <span style={{ fontSize: 20 }}>💎</span>
                    <div>
                      <Text weight="medium" style={{ fontSize: 14, display: 'block', marginBottom: 2 }}>
                        Самые низкие цены в премиум-пакетах
                      </Text>
                      <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                        До 60% выгоды при покупке больших пакетов
                      </Text>
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: '16px',
                  background: 'var(--vkui--color_background_tertiary)',
                  borderRadius: 12,
                  textAlign: 'center'
                }}>
                  <Text style={{
                    fontSize: 14,
                    color: 'var(--vkui--color_text_secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}>
                    <span>💡</span>
                    <span>Оплачиваются только ответы персонажей. Твои сообщения — бесплатны!</span>
                  </Text>
                </div>

                {/* Return to Character Button */}
                {selectedCharacter && (
                  <Button
                    size="l"
                    mode="secondary"
                    stretched
                    onClick={() => setActivePanel('chat')}
                    style={{ marginTop: 16 }}
                    before={<span>←</span>}
                  >
                    Вернуться к {selectedCharacter.name}
                  </Button>
                )}
              </Div>
            </Group>

            <Group>
              <Div>
                <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 13, textAlign: 'center' }}>
                  Платежи обрабатываются через ВКонтакте голосами.
                  Сообщения зачисляются автоматически.
                </Text>
              </Div>
            </Group>
          </Panel>

          <Panel id="terms">
            <PanelHeader before={<div style={{ marginLeft: 4 }}><PanelHeaderBack onClick={() => setActivePanel('main')} /></div>}>
              Пользовательское соглашение
            </PanelHeader>
            <TermsPanel onBack={() => setActivePanel('main')} />
          </Panel>

          <Panel id="privacy">
            <PanelHeader before={<div style={{ marginLeft: 4 }}><PanelHeaderBack onClick={() => setActivePanel('main')} /></div>}>
              Политика конфиденциальности
            </PanelHeader>
            <PrivacyPanel onBack={() => setActivePanel('main')} />
          </Panel>

          <Panel id="subscription">
            <PanelHeader before={<div style={{ marginLeft: 4 }}><PanelHeaderBack onClick={() => setActivePanel('main')} /></div>}>
              ⭐ Подписки
            </PanelHeader>
            <Group>
              <Div>
                <Text style={{
                  fontSize: 16,
                  fontWeight: 'medium',
                  marginBottom: 12,
                  color: 'var(--vkui--color_text_primary)'
                }}>
                  Разовая оплата открывает тариф на 30 дней: ежедневные сообщения, скидки на пакеты и бонусы.
                </Text>
                <Div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 0 }}>
                  {/* Free plan */}
                  <Div style={{
                    padding: '16px',
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #4a90d9 0%, #5e4ad9 100%)',
                    color: 'white'
                  }}>
                    <Text weight="semibold" style={{ fontSize: 16, marginBottom: 8, display: 'block', color: 'white' }}>
                      Бесплатный — Бесплатно
                    </Text>
                    <Text style={{ fontSize: 13, marginBottom: 8, display: 'block', color: 'rgba(255,255,255,0.9)' }}>
                      Ежедневные сообщения со стрик-системой
                    </Text>
                    {['День 1: 2 сообщения', 'День 2: 3 сообщения', 'День 3: 4 сообщения', 'День 4+: 5 сообщений (максимум)', 'Пропустил день → стрик сбрасывается', 'До ~150 сообщений/мес при ежедневном входе'].map((item, idx) => (
                      <Text key={idx} style={{ fontSize: 13, display: 'block', color: 'rgba(255,255,255,0.9)', marginBottom: 2 }}>
                        ▫️ {item}
                      </Text>
                    ))}
                  </Div>

                  {/* Paid plans from store */}
                  {subscriptions.length === 0 && (
                    <Text style={{ fontSize: 14, color: 'var(--vkui--color_text_secondary)', textAlign: 'center' }}>
                      Подписки временно недоступны
                    </Text>
                  )}
                  {subscriptions.map((plan) => (
                    <Div key={plan.id} style={{
                      padding: '16px',
                      borderRadius: 12,
                      background: 'var(--vkui--color_background_secondary)',
                      color: 'var(--vkui--color_text_primary)'
                    }}>
                      <Text weight="semibold" style={{ fontSize: 16, marginBottom: 4, display: 'block' }}>
                        {plan.name} — {plan.price_vk_votes} голосов
                      </Text>
                      <Text style={{ fontSize: 13, marginBottom: 8, display: 'block', color: 'var(--vkui--color_text_secondary)' }}>
                        До {plan.messages_per_month} сообщений за 30 дней — ежедневно +{plan.daily_bonus || Math.max(1, Math.floor(plan.messages_per_month / 30))} сообщ.
                      </Text>
                      {plan.perks && plan.perks.length > 0 && (
                        <Div style={{ padding: 0, marginBottom: 12 }}>
                          {plan.perks.map((perk, idx) => (
                            <Text key={idx} style={{ fontSize: 13, display: 'block', color: 'var(--vkui--color_text_secondary)', marginBottom: 2 }}>
                              ▫️ {perk}
                            </Text>
                          ))}
                        </Div>
                      )}
                      <Button
                        mode="primary"
                        size="m"
                        stretched
                        onClick={() => {
                          if (!vkUserId) return;
                          handlePurchase({ itemId: `sub_${plan.id}`, title: plan.name, isSubscription: true, expectedPlanId: plan.id });
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #4a90d9 0%, #5e4ad9 100%)',
                          color: 'white'
                        }}
                      >
                        Оформить за {plan.price_vk_votes} голосов
                      </Button>
                    </Div>
                  ))}
                </Div>
                <Div style={{
                  padding: '16px',
                  background: 'var(--vkui--color_background_tertiary)',
                  borderRadius: 12,
                  marginTop: 12
                }}>
                  <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                    ⚠️ Важно:<br />
                    • Ежедневные сообщения начисляются каждый день<br />
                    • Получить их можно через кнопку в главном меню или просто пользоваться ботом<br />
                    • Если не забрать ежедневное начисление, оно не переносится на следующий день
                  </Text>
                </Div>
              </Div>
            </Group>
          </Panel>

          <Panel id="referral">
            <PanelHeader before={<div style={{ marginLeft: 4 }}><PanelHeaderBack onClick={() => setActivePanel('main')} /></div>}>
              👥 Реферальная программа
            </PanelHeader>
            <Group>
              <Div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
                <Text weight="semibold" style={{
                  fontSize: 20,
                  marginBottom: 8,
                  display: 'block'
                }}>
                  👥 Пригласи друга в уникальный мир диалогов!
                </Text>
                <Text style={{
                  fontSize: 15,
                  color: 'var(--vkui--color_text_secondary)',
                  lineHeight: '1.4',
                  display: 'block',
                  marginBottom: 16
                }}>
                  ✨ Подари другу возможность увлекательного общения с нашими персонажами и получи щедрые вознаграждения для еще более глубокого погружения в диалоги.<br /><br />
                  🎁 Реферальная программа:<br />
                  ▫️ 🎯 За регистрацию друга: вы оба получаете по 1 сообщению!<br />
                  ▫️ 💎 За первую оплату друга: ты получаешь гарантированно 1 сообщение + 100% от суммы его пополнения!<br />
                  ▫️ 🔥 Сообщения не сгорают — используй для любых персонажей<br /><br />
                  💛 Мы ценим искренность<br />
                  Пожалуйста, приглашай только реальных друзей, которые действительно хотят пообщаться. Наша система проверяет активность новичков, чтобы убедиться, что им интересно. Честные приглашения приносят радость всем!<br /><br />
                  ⚠️ Важно: злоупотребление доверием приведёт к ограничению данного функционала и снижению реферальных бонусов.<br /><br />
                  📌 Лимит: до 5 друзей в месяц
                </Text>
                <Div style={{
                  padding: '12px',
                  background: 'var(--vkui--color_background_tertiary)',
                  borderRadius: 8,
                  marginBottom: 16,
                  textAlign: 'left'
                }}>
                  <Text style={{
                    fontSize: 13,
                    color: 'var(--vkui--color_text_secondary)',
                    lineHeight: '1.4'
                  }}>
                    ⚠️ Бонусы за рефералов действуют честно. При обнаружении подозрительной активности аккаунт может быть ограничен в программе. Если ты не согласен с решением — напиши в сообщество техподдержки.
                  </Text>
                </Div>
                {referralInfo?.code && (
                  <Div style={{
                    background: 'var(--vkui--color_background_secondary)',
                    borderRadius: 12,
                    padding: '16px',
                    marginBottom: 16
                  }}>
                    <Text style={{
                      fontSize: 13,
                      color: 'var(--vkui--color_text_secondary)',
                      marginBottom: 4,
                      display: 'block'
                    }}>
                      Твой реферальный код
                    </Text>
                    <Text weight="semibold" style={{
                      fontSize: 24,
                      color: 'var(--vkui--color_accent_violet)',
                      display: 'block',
                      marginBottom: 12
                    }}>
                      {referralInfo.code}
                    </Text>
                    <Button
                      size="l"
                      mode="primary"
                      stretched
                      onClick={handleShareReferral}
                    >
                      Поделиться ссылкой
                    </Button>
                    <Text style={{
                      fontSize: 12,
                      color: 'var(--vkui--color_text_secondary)',
                      marginTop: 8,
                      display: 'block',
                      wordBreak: 'break-all'
                    }}>
                      {referralInfo?.link || `https://vk.com/app${appId}#ref_${referralInfo.code}`}
                    </Text>
                  </Div>
                )}
                {referralInfo?.referrals && (
                  <Div style={{
                    display: 'grid',
                    gap: 12,
                    textAlign: 'left'
                  }}>
                    <Div style={{
                      padding: '12px',
                      background: 'var(--vkui--color_background_secondary)',
                      borderRadius: 8
                    }}>
                      <Text weight="medium" style={{ fontSize: 14, display: 'block', marginBottom: 2 }}>
                        Приглашено друзей
                      </Text>
                      <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                        {referralInfo.referrals.total_referrals} / {referralInfo.referrals.max_referrals_per_month} в этом месяце
                      </Text>
                    </Div>
                    {referralInfo.antifraud?.is_bot_heavy && (
                      <Div style={{
                        padding: '12px',
                        background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                        borderRadius: 8,
                        color: 'white'
                      }}>
                        <Text weight="medium" style={{ fontSize: 14, display: 'block', marginBottom: 2, color: 'white' }}>
                          ⚠️ Антифрод ограничение
                        </Text>
                        <Text style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)' }}>
                          Обнаружена подозрительная активность. Фиксированный бонус за первую оплату временно не начисляется; сообщения, купленные рефералом, учитываются полностью.
                        </Text>
                      </Div>
                    )}
                  </Div>
                )}
              </Div>
            </Group>
          </Panel>
        </View>
      </SplitCol>

    </SplitLayout>
  );
};

export default App;
